import React, { useState, useEffect, useRef, useMemo } from 'react';

// ============================================================================
// Brand & Subdomain Asset Registry (Place your files in `public/brand/`)
// ============================================================================
export const BRAND_ASSETS = {
  mainLogo: '/brand/logo-main.png',
  galaxyDesktop: '/brand/galaxy-desktop.jpg',
  galaxyMobile: '/brand/galaxy-mobile.jpg',
  subdomains: {
    drainage: '/brand/logo-drainage.png',
    borehole: '/brand/logo-borehole.png',
    slope: '/brand/logo-slope.png',
    retaining: '/brand/logo-retaining.png',
  } as Record<string, string>,
};

// ============================================================================
// Types matching FastAPI v3.0 & v3.1 Pipeline (/api/kb/ask & /api/kb/documents)
// ============================================================================
export interface CitationItem {
  chunk_id: number | string;
  doc_slug: string;
  doc_title: string;
  authority?: string | null;
  doc_category?: string | null;
  source_tier?: number;
  clause_id?: string | null;
  clause_number?: string | null;
  clause_title?: string | null;
  section_breadcrumb?: string | null;
  page_number: number;
  chunk_type: string;
  content: string;
  pdf_url: string;
  bbox_json?: Record<string, any> | null;
  figure_url?: string | null;
  figure_caption?: string | null;
  extracted_axes_json?: Record<string, any> | null;
  markdown_payload?: string | null;
  score: number;
  // v3.1 optional edition/status metadata (backwards-compatible)
  edition_label?: string | null;
  edition_year?: number | null;
  status?: string | null;
  amended_by?: string[] | null;
}

interface LinkedAssetRaw {
  asset_id?: string;
  asset_type?: string;
  asset_label?: string;
  caption?: string | null;
  page_no?: number;
  markdown_payload?: string | null;
  image_url?: string | null;
  pdf_page_url?: string;
}

interface IngestedDocumentItem {
  doc_code: string;
  doc_title: string;
  domain: string;
  authority?: string;
  doc_category?: string;
  source_tier?: number;
  edition_year?: number;
  edition_label?: string;
  status?: string;
  pdf_url: string;
  total_clauses: number;
  total_assets: number;
}

export interface ChatAttachment {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  kind: 'image' | 'pdf' | 'code' | 'data' | 'text' | 'document';
  previewUrl?: string;
  extractedText?: string;
  imageDataUrl?: string;
  extraImageUrls?: string[];
  status: 'ready' | 'uploading' | 'error';
}

type ExecutedTierNumber = 1 | 2 | 3;

interface TierVisualMeta {
  tier: ExecutedTierNumber;
  shortLabel: string;
  badgeLabel: string;
  modelName: string;
  dotColor: string;
}

export interface ProposedMcpAction {
  tool_name: string;
  tool_id?: string;
  server: 'drainage_mcp' | 'borehole_mcp' | 'core_calc_mcp' | string;
  chained_tools?: string[];
  extracted_inputs?: Record<string, any>;
  assumed_defaults?: Record<string, any>;
  result?: Record<string, any>;
  ui_launch_url?: string | null;
  editor_v5_project?: Record<string, any> | null;
  kb_binding_flags?: Array<Record<string, any>> | null;
}

export interface InteractiveArtifactItem {
  artifact_id: string;
  artifact_type: 'interactive_chart' | 'parameter_calculator' | 'design_checklist' | string;
  title: string;
  subtitle?: string | null;
  governing_code?: string | null;
  chart_spec?: Record<string, any> | null;
  interactive_controls?: Array<{
    param: string;
    value: any;
    editable?: boolean;
  }>;
  data_table?: Record<string, any> | null;
  mcp_tool_endpoint?: string | null;
}

export interface WorkflowBlueprintItem {
  component_key: string;
  component_name: string;
  domain: string;
  current_timeline_stage: string;
  governing_codes: string[];
  info_gathering_checklist: Array<Record<string, any>>;
  critical_stages: Array<Record<string, any>>;
  mandatory_checks: Array<Record<string, any>>;
  linked_mcp_tools: string[];
  report_sections: string[];
}

export interface SourceCheckInfo {
  verdict?: 'pass' | 'warn' | 'fail' | string;
  notes?: string[];
  dropped_chunk_ids?: Array<number | string>;
  flagged_citations?: string[];
  untraced_fos_values?: Array<number | string>;
  superseded_sources?: string[];
  amended_clauses?: string[];
  flagged_tool_constants?: string[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  tierExecuted?: ExecutedTierNumber;
  epistemicRoute?: string;
  queryDepth?: string;
  queryCategory?: string;
  modelUsed?: string;
  synthesisRole?: string;
  configVersion?: number | string;
  routingReason?: string;
  thoughtSummary?: string[];
  confidenceScore?: number;
  expandedQuery?: string;
  citations?: CitationItem[];
  attachments?: ChatAttachment[];
  proposedMcpAction?: ProposedMcpAction | null;
  interactiveArtifacts?: InteractiveArtifactItem[];
  workflowBlueprint?: WorkflowBlueprintItem | null;
  sourceChecks?: SourceCheckInfo | null;
  sourceCheckVerdict?: string | null;
}

interface ChatSession {
  id: string;
  title: string;
  updatedAt: number;
  lastTierExecuted: ExecutedTierNumber;
  designState?: Record<string, any>;
  messages: ChatMessage[];
}

export interface LandingProps {
  onEnter?: (selectedTool: string) => void;
  onSelectModule?: (module: 'drainage' | 'borehole') => void;
  userEmail?: string | null;
  onOpenAuth?: (reason?: string, onUnlocked?: () => void) => void;
  onSignOut?: () => void;
}

const STORAGE_KEY = 'civeng_poe_chats_v3';

const TIER_VISUALS: Record<ExecutedTierNumber, TierVisualMeta> = {
  1: {
    tier: 1,
    shortLabel: 'Tier 1 · Fast Router',
    badgeLabel: 'Tier 1 · DeepSeek-v4.1-Flash',
    modelName: 'deepseek/deepseek-v4.1-flash-1017',
    dotColor: '#38bdf8',
  },
  2: {
    tier: 2,
    shortLabel: 'Tier 2 · Code Synthesis',
    badgeLabel: 'Tier 2 · GLM-5.3-Flash',
    modelName: 'z-ai/glm-5.3-flash',
    dotColor: '#06b6d4',
  },
  3: {
    tier: 3,
    shortLabel: 'Tier 3 · Deep Engineering Audit',
    badgeLabel: 'Tier 3 · Kimi-K3.1-Pro',
    modelName: 'moonshotai/kimi-k3.1-pro',
    dotColor: '#a855f7',
  },
};

const LOADING_THOUGHT_STAGES = [
  {
    stage: 'Stage 1 · Epistemic Router & Registry',
    detail: 'Classifying engineering depth (L1/L2/L3), resolving HK code aliases & work editions...',
  },
  {
    stage: 'Stage 2 · Hybrid Clause Retrieval & FastMCP',
    detail: 'Searching pgvector HNSW + lexical tsvector channels, checking clause overlays & tool constants...',
  },
  {
    stage: 'Stage 3 · Statutory Synthesis & Source Checks',
    detail: 'Synthesizing clause-grounded calculations and verifying Factor of Safety (FOS) citations...',
  },
];

const ENGINEERING_TOOLS = [
  {
    id: 'drainage',
    code: 'YLS',
    name: 'YLS Surface Drainage',
    subtitle: 'Rational Method, Catchment & U-Channel Network Studio',
    badge: 'DSD SDM 2018',
    statusTag: '✓',
    accent: '#38bdf8',
    bg: 'linear-gradient(135deg, #0284c7, #0369a1)',
    active: true,
  },
  {
    id: 'borehole',
    code: 'BD',
    name: '3D Boreholes & GI BIM',
    subtitle: 'HK AGS4 Parser, Stratigraphy Logs & Subsurface Mesh',
    badge: 'GEO / AGS 4.0',
    statusTag: '✓',
    accent: '#f97316',
    bg: 'linear-gradient(135deg, #ea580c, #c2410c)',
    active: true,
  },
  {
    id: 'slope',
    code: 'SL',
    name: 'Slope Upgrading',
    subtitle: 'Geoguide 7 Soil Nail & Surface Protection Check',
    badge: 'GEO GG7',
    statusTag: 'GG7',
    accent: '#14b8a6',
    bg: 'linear-gradient(135deg, #0d9488, #115e59)',
    active: true,
  },
  {
    id: 'retaining',
    code: 'RE',
    name: 'Retaining Wall',
    subtitle: 'Geoguide 1 Sliding, Overturning & ELS Kickout Check',
    badge: 'GEO GG1',
    statusTag: 'GG1',
    accent: '#8b5cf6',
    bg: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
    active: true,
  },
];

const DEFAULT_FALLBACK_MANUALS: IngestedDocumentItem[] = [
  {
    doc_code: 'DSD SDM 2018',
    doc_title: 'Stormwater Drainage Manual (5th Edition, 2018)',
    domain: 'drainage',
    edition_year: 2018,
    pdf_url: '/kb-assets/pdfs/dsd_sdm_2018.pdf#page=1',
    total_clauses: 204,
    total_assets: 0,
  },
];

// ============================================================================
// Reusable Brand / Subdomain Icon Component with Automatic Image -> Badge Fallback
// ============================================================================
const BrandIcon: React.FC<{
  src?: string;
  fallbackText: string;
  size?: number;
  borderRadius?: number;
  gradient?: string;
  glowColor?: string;
}> = ({
  src,
  fallbackText,
  size = 32,
  borderRadius = 9,
  gradient = 'linear-gradient(135deg, #0284c7, #2563eb)',
  glowColor = 'rgba(56, 189, 248, 0.45)',
}) => {
  const [imgFailed, setImgFailed] = useState<boolean>(false);

  if (src && !imgFailed) {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: `${borderRadius}px`,
          overflow: 'hidden',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: gradient,
          border: '1px solid rgba(125, 211, 252, 0.4)',
          boxShadow: `0 0 14px ${glowColor}`,
        }}
      >
        <img
          src={src}
          alt={fallbackText}
          onError={() => setImgFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${borderRadius}px`,
        background: gradient,
        border: '1px solid rgba(125, 211, 252, 0.45)',
        boxShadow: `0 0 14px ${glowColor}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: `${Math.max(10, Math.round(size * 0.36))}px`,
        color: '#ffffff',
        flexShrink: 0,
        letterSpacing: '-0.02em',
      }}
    >
      {fallbackText}
    </div>
  );
};

// ============================================================================
// Mobile-Compatible PDF Page Renderer (Uses PDF.js on Mobile so exact page renders)
// Fixes mobile browsers ignoring `#page=N` inside PDF iframes
// ============================================================================
const PDFJS_CDN_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

let pdfJsLoadPromise: Promise<any> | null = null;
function loadPdfJsLibrary(): Promise<any> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  const win = window as any;
  if (win.pdfjsLib) {
    win.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
    return Promise.resolve(win.pdfjsLib);
  }
  if (pdfJsLoadPromise) return pdfJsLoadPromise;

  pdfJsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_CDN_URL;
    script.async = true;
    script.onload = () => {
      if (win.pdfjsLib) {
        win.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        resolve(win.pdfjsLib);
      } else {
        reject(new Error('PDF.js failed to initialize'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js from CDN'));
    document.head.appendChild(script);
  });

  return pdfJsLoadPromise;
}

const MobilePdfPageViewer: React.FC<{
  pdfUrl: string;
  pageNumber: number;
  docTitle: string;
  onTotalPages?: (total: number) => void;
}> = ({ pdfUrl, pageNumber, docTitle, onTotalPages }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null);
  const loadedUrlRef = useRef<string>('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [zoom, setZoom] = useState<number>(1.0);

  const cleanPdfPath = useMemo(() => {
    if (!pdfUrl) return '';
    return pdfUrl.split('#')[0].split('?')[0].trim();
  }, [pdfUrl]);

  useEffect(() => {
    let cancelled = false;
    let renderTask: any = null;

    const renderTargetPage = async () => {
      if (!cleanPdfPath) return;
      setStatus('loading');
      setErrorMsg('');

      try {
        const pdfjsLib = await loadPdfJsLibrary();
        if (cancelled) return;

        if (!pdfDocRef.current || loadedUrlRef.current !== cleanPdfPath) {
          const loadingTask = pdfjsLib.getDocument({
            url: cleanPdfPath,
            cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
            cMapPacked: true,
          });
          pdfDocRef.current = await loadingTask.promise;
          loadedUrlRef.current = cleanPdfPath;
        }

        if (cancelled) return;
        const pdfDoc = pdfDocRef.current;
        const numPages = pdfDoc.numPages || 1;
        onTotalPages?.(numPages);

        const targetPage = Math.min(Math.max(1, pageNumber || 1), numPages);
        const page = await pdfDoc.getPage(targetPage);
        if (cancelled) return;

        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const containerWidth = Math.max(280, container.clientWidth - 16);
        const unscaledViewport = page.getViewport({ scale: 1 });
        const baseScale = (containerWidth / unscaledViewport.width) * zoom;
        const dpr = Math.min(window.devicePixelRatio || 1, 2.25);
        const viewport = page.getViewport({ scale: baseScale * dpr });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        renderTask = page.render({
          canvasContext: ctx,
          viewport,
        });
        await renderTask.promise;
        if (!cancelled) {
          setStatus('ready');
        }
      } catch (err: any) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(err?.message || 'Unable to render PDF page.');
        }
      }
    };

    renderTargetPage();

    return () => {
      cancelled = true;
      if (renderTask && typeof renderTask.cancel === 'function') {
        try {
          renderTask.cancel();
        } catch {}
      }
    };
  }, [cleanPdfPath, pageNumber, zoom]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#0b0f19',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '8px',
        position: 'relative',
      }}
    >
      {/* Zoom & Native Open Toolbar for Mobile */}
      <div
        style={{
          position: 'sticky',
          top: '4px',
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '999px',
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          border: '1px solid rgba(148, 163, 184, 0.22)',
          marginBottom: '8px',
          backdropFilter: 'blur(8px)',
        }}
      >
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(0.75, Number((z - 0.25).toFixed(2))))}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#cbd5e1',
            fontSize: '13px',
            fontWeight: 700,
            padding: '2px 6px',
            cursor: 'pointer',
          }}
        >
          −
        </button>
        <span className="eng-mono" style={{ fontSize: '11px', color: '#7dd3fc' }}>
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(2.5, Number((z + 0.25).toFixed(2))))}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#cbd5e1',
            fontSize: '13px',
            fontWeight: 700,
            padding: '2px 6px',
            cursor: 'pointer',
          }}
        >
          ＋
        </button>
      </div>

      {status === 'loading' && (
        <div
          style={{
            padding: '32px 16px',
            color: '#94a3b8',
            fontSize: '12.5px',
            textAlign: 'center',
          }}
        >
          Rendering {docTitle} — Page {pageNumber}...
        </div>
      )}

      {status === 'error' && (
        <div
          style={{
            padding: '24px 16px',
            color: '#cbd5e1',
            fontSize: '12.5px',
            textAlign: 'center',
            maxWidth: '320px',
          }}
        >
          <div style={{ marginBottom: '10px', color: '#fda4af' }}>
            {errorMsg}
          </div>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '7px 14px',
              borderRadius: '8px',
              backgroundColor: 'rgba(56, 189, 248, 0.2)',
              border: '1px solid rgba(56, 189, 248, 0.45)',
              color: '#7dd3fc',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            Open PDF Page {pageNumber} in Browser ↗
          </a>
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{
          display: status === 'error' ? 'none' : 'block',
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.55)',
          backgroundColor: '#ffffff',
        }}
      />
    </div>
  );
};

// ============================================================================
// Interactive Galaxy Starfield & Shooting Meteor Canvas Overlay
// ============================================================================
const InteractiveGalaxyCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId = 0;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    let mouseX = width * 0.5;
    let mouseY = height * 0.35;
    let targetMouseX = mouseX;
    let targetMouseY = mouseY;

    const starCount = width < 768 ? 55 : 110;
    const stars = Array.from({ length: starCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: Math.random() * 1.45 + 0.35,
      alpha: Math.random() * 0.65 + 0.2,
      twinkleSpeed: Math.random() * 0.018 + 0.004,
      phase: Math.random() * Math.PI * 2,
      depth: Math.random() * 0.8 + 0.2,
      hue: Math.random() > 0.7 ? '#7dd3fc' : Math.random() > 0.85 ? '#fcd34d' : '#f8fafc',
    }));

    interface Meteor {
      x: number;
      y: number;
      len: number;
      speed: number;
      angle: number;
      alpha: number;
      active: boolean;
    }

    const meteor: Meteor = {
      x: 0,
      y: 0,
      len: 130,
      speed: 11,
      angle: Math.PI * 0.78,
      alpha: 0,
      active: false,
    };

    const spawnMeteor = () => {
      meteor.x = width * (0.35 + Math.random() * 0.55);
      meteor.y = height * (0.04 + Math.random() * 0.28);
      meteor.len = 110 + Math.random() * 80;
      meteor.speed = 9 + Math.random() * 5;
      meteor.angle = Math.PI * (0.76 + (Math.random() - 0.5) * 0.08);
      meteor.alpha = 0.95;
      meteor.active = true;
    };

    const meteorTimer = window.setInterval(() => {
      if (!meteor.active && Math.random() > 0.25) {
        spawnMeteor();
      }
    }, 5200);

    const onResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const onMouseMove = (e: MouseEvent) => {
      targetMouseX = e.clientX;
      targetMouseY = e.clientY;
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    const render = (t: number) => {
      ctx.clearRect(0, 0, width, height);

      mouseX += (targetMouseX - mouseX) * 0.04;
      mouseY += (targetMouseY - mouseY) * 0.04;

      const offsetX = ((mouseX - width / 2) / width) * 18;
      const offsetY = ((mouseY - height / 2) / height) * 18;

      const grad = ctx.createRadialGradient(mouseX, mouseY, 8, mouseX, mouseY, 260);
      grad.addColorStop(0, 'rgba(56, 189, 248, 0.055)');
      grad.addColorStop(0.5, 'rgba(99, 102, 241, 0.025)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      for (const st of stars) {
        const twinkle = Math.sin(t * st.twinkleSpeed + st.phase) * 0.28 + 0.72;
        const sx = (st.x + offsetX * st.depth + width) % width;
        const sy = (st.y + offsetY * st.depth + height) % height;

        ctx.beginPath();
        ctx.arc(sx, sy, st.r, 0, Math.PI * 2);
        ctx.fillStyle = st.hue;
        ctx.globalAlpha = Math.max(0.08, Math.min(0.95, st.alpha * twinkle));
        ctx.fill();
      }

      if (meteor.active) {
        ctx.save();
        ctx.globalAlpha = meteor.alpha;
        const tailX = meteor.x - Math.cos(meteor.angle) * meteor.len;
        const tailY = meteor.y - Math.sin(meteor.angle) * meteor.len;

        const mGrad = ctx.createLinearGradient(meteor.x, meteor.y, tailX, tailY);
        mGrad.addColorStop(0, 'rgba(224, 242, 254, 0.95)');
        mGrad.addColorStop(0.3, 'rgba(56, 189, 248, 0.55)');
        mGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');

        ctx.strokeStyle = mGrad;
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(meteor.x, meteor.y);
        ctx.lineTo(tailX, tailY);
        ctx.stroke();
        ctx.restore();

        meteor.x += Math.cos(meteor.angle) * meteor.speed;
        meteor.y += Math.sin(meteor.angle) * meteor.speed;
        meteor.alpha -= 0.016;
        if (meteor.alpha <= 0 || meteor.x < -100 || meteor.y > height + 100) {
          meteor.active = false;
        }
      }

      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(meteorTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
};

// ============================================================================
// Client-Side Sanitizer & True LaTeX Formatter (KaTeX + Stacked Fractions)
// ============================================================================
function healCorruptedLatexEscapes(input: string): string {
  if (!input) return '';
  return (
    input
      .replace(/\f(rac\b)/g, '\\f$1')
      .replace(/\t(ext\b|imes\b|heta\b|au\b|an\b|o\b|op\b|ilde\b)/g, '\\t$1')
      .replace(/[\b](eta\b|ar\b|egin\b|inom\b)/g, '\\b$1')
      .replace(/\\t\s*\(([0-9.]+)\)/g, '   ($1)')
      .replace(/\\t(?![a-zA-Z])/g, ' ')
      .replace(/(^|[^\\a-zA-Z])(?:d|t)?rac\{/g, '$1\\frac{')
      .replace(/(^|[^\\a-zA-Z])ext\{/g, '$1\\text{')
      .replace(/\\?\s+ext\{/g, '\\text{')
      .replace(/\\?\s+imes\b/g, '\\times')
      .replace(/\\?\s+heta\b/g, '\\theta')
      .replace(/\\?\s+au\b/g, '\\tau')
      .replace(/\\?\s+frac\{/g, '\\frac{')
  );
}

function sanitizeAssistantMarkdown(rawText: string): string {
  if (!rawText) return '';
  let s = healCorruptedLatexEscapes(rawText).trim();

  s = s.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();

  const isRawEnvelope = s.startsWith('{') && s.includes('"answer_markdown"');
  if (isRawEnvelope) {
    try {
      const parsed = JSON.parse(s);
      if (parsed && typeof parsed.answer_markdown === 'string') {
        s = parsed.answer_markdown;
      }
    } catch {
      const m = s.match(/"answer_markdown"\s*:\s*"((?:\\.|[^"\\])*)"/s);
      if (m && m[1]) {
        s = m[1];
      } else {
        const splitParts = s.split(/"answer_markdown"\s*:\s*"/);
        if (splitParts.length > 1) {
          s = splitParts[1]
            .replace(/",\s*"cited_chunk_ids"[\s\S]*$/, '')
            .replace(/"\s*\}\s*$/, '');
        }
      }
    }
    s = s
      .replace(/\\n(?![a-zA-Z])/g, '\n')
      .replace(/\\t(?![a-zA-Z])/g, '  ')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  } else if (!s.includes('\n') && s.includes('\\n')) {
    s = s.replace(/\\n(?![a-zA-Z])/g, '\n');
  }

  s = healCorruptedLatexEscapes(s);

  s = s
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, inner) => `$$${inner}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, inner) => `$${inner}$`);

  s = s.replace(
    /^(\s*)(?:Q\s*=\s*C\s*[*·×]?\s*i\s*[*·×]?\s*A\s*\/\s*360)(\s*)$/gm,
    '$1$$Q = \\frac{C \\cdot i \\cdot A}{360}$$2'
  );

  s = s.replace(/^#\s*(\|.*\|)$/gm, '$1');
  s = s.replace(/([^\n])\n(#{1,4}\s+)/g, '$1\n\n$2');
  return s.trim();
}

function normalizeEngineeringLatex(raw: string, displayMode: boolean): string {
  let s = healCorruptedLatexEscapes(raw).trim();

  s = s
    .replace(/\s*\*\s*/g, ' \\cdot ')
    .replace(/<=|≤/g, ' \\le ')
    .replace(/>=|≥/g, ' \\ge ')
    .replace(/!=/g, ' \\ne ')
    .replace(/\bphi'/g, "\\phi'")
    .replace(/\bgamma_w\b/g, '\\gamma_{w}')
    .replace(/\btau_ult\b/g, '\\tau_{ult}')
    .replace(/\bFOS\b/g, '\\text{FOS}');

  s = s.replace(/_([a-zA-Z0-9]{2,})\b/g, '_{$1}');

  if (!s.includes('\\frac')) {
    s = s.replace(/\(([^()]+)\)\s*\/\s*\(([^()]+)\)/g, '\\frac{$1}{$2}');
    if (displayMode) {
      s = s.replace(
        /([A-Za-z0-9_\\{}'\scdot+-]+)\s*\/\s*([0-9A-Za-z_\\{}']+)(?=\s*(?:$|=|,|\)))/g,
        (full, num, den) => {
          if (num.includes('=') && !num.trim().startsWith('(')) {
            const eqParts = num.split('=');
            const lhs = eqParts.slice(0, -1).join('=');
            const rhsNum = eqParts[eqParts.length - 1].trim();
            if (rhsNum) return `${lhs} = \\frac{${rhsNum}}{${den.trim()}}`;
          }
          return full;
        }
      );
    }
  }

  return s;
}

function renderLatexNode(latex: string, displayMode: boolean): React.ReactNode {
  const cleanLatex = normalizeEngineeringLatex(latex, displayMode);
  const katexObj = typeof window !== 'undefined' ? (window as any).katex : null;

  if (katexObj && typeof katexObj.renderToString === 'function') {
    try {
      const html = katexObj.renderToString(cleanLatex, {
        displayMode,
        throwOnError: false,
        strict: false,
      });
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    } catch {
      // Fallback below
    }
  }

  return (
    <span
      dangerouslySetInnerHTML={{
        __html: formatEngineeringMathHtml(cleanLatex, displayMode),
      }}
    />
  );
}

function formatEngineeringMathHtml(latex: string, displayMode: boolean): string {
  let m = healCorruptedLatexEscapes(latex)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .trim();

  for (let pass = 0; pass < 4; pass++) {
    m = m.replace(
      /\\(?:text|mathrm|operatorname)\{([^{}]*)\}/g,
      '<span style="font-style:normal;font-family:var(--font-sans)">$1</span>'
    );
    m = m.replace(/\\mathbf\{([^{}]*)\}/g, '<strong>$1</strong>');
  }

  const greekMap: Array<[RegExp, string]> = [
    [/\\phi'/g, "φ'"],
    [/\\varphi\b|\\phi\b/g, 'φ'],
    [/\\gamma_\{?w\}?/g, 'γ<sub>w</sub>'],
    [/\\gamma'/g, "γ'"],
    [/\\gamma\b/g, 'γ'],
    [/\\sigma'/g, "σ'"],
    [/\\sigma\b/g, 'σ'],
    [/\\tau\b/g, 'τ'],
    [/\\delta\b/g, 'δ'],
    [/\\pi\b/g, 'π'],
    [/\\alpha\b/g, 'α'],
    [/\\beta\b/g, 'β'],
    [/\\theta\b/g, 'θ'],
    [/\\rho\b/g, 'ρ'],
    [/\\lambda\b/g, 'λ'],
    [/\\mu\b/g, 'μ'],
    [/\\Delta\b/g, 'Δ'],
    [/\\sum\b/g, '∑'],
    [/\\times\b/g, '×'],
    [/\\cdot\b/g, '·'],
    [/\\leq\b|\\le\b/g, '≤'],
    [/\\geq\b|\\ge\b/g, '≥'],
    [/\\neq\b|\\ne\b/g, '≠'],
    [/\\pm\b/g, '±'],
    [/\\approx\b/g, '≈'],
    [/\\circ\b/g, '°'],
    [/\\rightarrow\b|\\to\b/g, '→'],
    [/\\Rightarrow\b/g, '⇒'],
    [/\\left|\\right/g, ''],
  ];
  for (const [pattern, replacement] of greekMap) {
    m = m.replace(pattern, replacement);
  }

  for (let pass = 0; pass < 3; pass++) {
    m = m
      .replace(/_\{([^{}]+)\}/g, '<sub>$1</sub>')
      .replace(/\^\{([^{}]+)\}/g, '<sup>$1</sup>')
      .replace(/_([a-zA-Z0-9]+)/g, '<sub>$1</sub>')
      .replace(/\^([a-zA-Z0-9]+)/g, '<sup>$1</sup>');
  }

  for (let pass = 0; pass < 4; pass++) {
    if (displayMode) {
      m = m.replace(
        /\\(?:d|t)?frac\{([^{}]+)\}\{([^{}]+)\}/g,
        '<span style="display:inline-flex;flex-direction:column;align-items:center;vertical-align:middle;margin:0 6px;line-height:1.25"><span style="border-bottom:1.5px solid #7dd3fc;padding:0 6px 2px 6px">$1</span><span style="padding:2px 6px 0 6px">$2</span></span>'
      );
    } else {
      m = m.replace(/\\(?:d|t)?frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1)/($2)');
    }
    m = m.replace(/\\sqrt\{([^{}]+)\}/g, '√($1)');
  }

  return m.replace(/\\\s+/g, ' ').trim();
}

function normalizePdfUrl(rawUrl: string | undefined | null, page: number): string {
  const safePage = page && page > 0 ? page : 1;
  if (!rawUrl || !rawUrl.trim()) {
    return '';
  }

  const [basePart] = rawUrl.split('#');
  let cleanPath = basePart.split('?')[0].trim();

  if (
    cleanPath.startsWith('/kb-assets/') &&
    !cleanPath.startsWith('/kb-assets/pdfs/') &&
    cleanPath.toLowerCase().endsWith('.pdf')
  ) {
    const fileName = cleanPath.replace('/kb-assets/', '').toLowerCase();
    cleanPath = `/kb-assets/pdfs/${fileName}`;
  }

  return `${cleanPath}?page=${safePage}#page=${safePage}&view=FitH`;
}

function normalizeBackendCitations(rawList: any[]): CitationItem[] {
  if (!Array.isArray(rawList)) return [];
  const results: CitationItem[] = [];

  rawList.forEach((raw, idx) => {
    const pageNum = Number(raw.page_number ?? raw.page_start ?? raw.page_no ?? 1) || 1;
    const rawPdf = raw.pdf_url ?? raw.pdf_page_url ?? '';
    const linkedAssets: LinkedAssetRaw[] = Array.isArray(raw.linked_assets)
      ? raw.linked_assets
      : [];
    const firstImageAsset = linkedAssets.find((a) => a.image_url);
    const firstTableAsset = linkedAssets.find((a) => a.markdown_payload);

    results.push({
      chunk_id: raw.chunk_id ?? `cit-${idx + 1}`,
      doc_slug: raw.doc_slug ?? raw.doc_code ?? 'HK Standard',
      doc_title: raw.doc_title ?? raw.doc_code ?? 'HK Engineering Manual',
      authority: raw.authority ?? raw.domain ?? 'HK Statutory Standard',
      doc_category: raw.doc_category ?? 'manual',
      source_tier: Number(raw.source_tier ?? 1),
      clause_id: raw.clause_number ?? raw.clause_id ?? raw.section_clause ?? null,
      clause_number: raw.clause_number ?? raw.clause_id ?? null,
      clause_title: raw.clause_title ?? raw.section_breadcrumb ?? raw.heading_path ?? null,
      section_breadcrumb: raw.section_breadcrumb ?? null,
      page_number: pageNum,
      chunk_type: raw.chunk_type ?? 'text_clause',
      content: raw.content ?? raw.content_md ?? '',
      pdf_url: normalizePdfUrl(rawPdf, pageNum),
      bbox_json: raw.bbox_json ?? null,
      figure_url: raw.figure_url ?? firstImageAsset?.image_url ?? null,
      figure_caption:
        raw.figure_caption ??
        firstImageAsset?.caption ??
        firstImageAsset?.asset_label ??
        firstTableAsset?.caption ??
        null,
      extracted_axes_json: raw.extracted_axes_json ?? null,
      markdown_payload: raw.markdown_payload ?? firstTableAsset?.markdown_payload ?? null,
      score: Number(raw.score ?? raw.rrf_score ?? 1.0),
      edition_label: raw.edition_label ?? null,
      edition_year: raw.edition_year ?? null,
      status: raw.status ?? null,
      amended_by: Array.isArray(raw.amended_by) ? raw.amended_by : null,
    });
  });

  return results;
}

function getTierMeta(tierNum?: number): TierVisualMeta {
  if (tierNum === 1) return TIER_VISUALS[1];
  if (tierNum === 3) return TIER_VISUALS[3];
  return TIER_VISUALS[2];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================================================
// Collapsible Thought Process & Pipeline Audit Trace Accordion
// ============================================================================
const ThoughtProcessTrace: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const tierMeta = getTierMeta(msg.tierExecuted);

  const hasDetails = Boolean(
    msg.routingReason ||
      msg.expandedQuery ||
      msg.epistemicRoute ||
      msg.queryDepth ||
      (msg.thoughtSummary && msg.thoughtSummary.length > 0) ||
      (msg.sourceChecks && msg.sourceChecks.notes && msg.sourceChecks.notes.length > 0)
  );

  if (!hasDetails) return null;

  return (
    <div
      style={{
        marginBottom: '12px',
        borderRadius: '10px',
        backgroundColor: 'rgba(15, 23, 42, 0.48)',
        border: '1px solid rgba(148, 163, 184, 0.14)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '7px 12px',
          background: 'transparent',
          border: 'none',
          color: '#94a3b8',
          fontSize: '11.5px',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <span
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: tierMeta.dotColor,
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 600, color: '#cbd5e1' }}>
            Thought Process & Engineering Route
          </span>
          <span className="eng-mono" style={{ fontSize: '10.5px', color: '#64748b' }}>
            {tierMeta.shortLabel}
            {msg.queryDepth ? ` · ${msg.queryDepth}` : ''}
            {msg.citations && msg.citations.length > 0
              ? ` · ${msg.citations.length} clauses verified`
              : ''}
          </span>
        </div>
        <span className="eng-mono" style={{ fontSize: '10px', color: '#64748b', flexShrink: 0 }}>
          {expanded ? 'Hide ▲' : 'Inspect ▼'}
        </span>
      </button>

      {expanded && (
        <div
          style={{
            padding: '8px 12px 10px 12px',
            borderTop: '1px solid rgba(148, 163, 184, 0.1)',
            fontSize: '11.5px',
            color: '#94a3b8',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            lineHeight: 1.5,
          }}
        >
          {msg.routingReason && (
            <div>
              <strong style={{ color: '#cbd5e1' }}>Router Rationale:</strong> {msg.routingReason}
            </div>
          )}
          {msg.expandedQuery && (
            <div>
              <strong style={{ color: '#cbd5e1' }}>Retrieval Query Expansion:</strong>{' '}
              <span className="eng-mono" style={{ fontSize: '11px', color: '#94a3b8' }}>
                {msg.expandedQuery}
              </span>
            </div>
          )}
          {msg.thoughtSummary &&
            msg.thoughtSummary.map((step, idx) => (
              <div key={idx}>• {step}</div>
            ))}
          <div
            className="eng-mono"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              fontSize: '10.5px',
              color: '#64748b',
              marginTop: '2px',
            }}
          >
            <span>Route: {msg.epistemicRoute || 'hybrid_kb'}</span>
            {msg.modelUsed && <span>Model: {msg.modelUsed}</span>}
            {msg.configVersion && <span>Retrieval Config: v{msg.configVersion}</span>}
            {msg.sourceCheckVerdict && (
              <span>Source Check Verdict: {msg.sourceCheckVerdict.toUpperCase()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// In-Chat Interactive Engineering Graph & Parameter Sensitivity Studio
// ============================================================================
const InteractiveEngineeringStudio: React.FC<{
  artifacts?: InteractiveArtifactItem[];
  proposedMcpAction?: ProposedMcpAction | null;
  onLaunchMcp?: (mcp: ProposedMcpAction) => void;
}> = ({ artifacts, proposedMcpAction, onLaunchMcp }) => {
  const calcArtifact = useMemo(
    () => artifacts?.find((a) => a.artifact_type === 'parameter_calculator'),
    [artifacts]
  );
  const chartArtifacts = useMemo(
    () => (artifacts || []).filter((a) => a.artifact_type === 'interactive_chart'),
    [artifacts]
  );

  const baseInputs = useMemo(() => {
    const raw =
      proposedMcpAction?.extracted_inputs ||
      (calcArtifact?.interactive_controls || []).reduce((acc: Record<string, any>, item) => {
        acc[item.param] = item.value;
        return acc;
      }, {});
    return raw || {};
  }, [proposedMcpAction, calcArtifact]);

  const [params, setParams] = useState<Record<string, number>>(() => {
    const numeric: Record<string, number> = {};
    Object.entries(baseInputs).forEach(([k, v]) => {
      const num = Number(v);
      if (!Number.isNaN(num) && Number.isFinite(num)) {
        numeric[k] = num;
      }
    });
    return numeric;
  });

  useEffect(() => {
    const numeric: Record<string, number> = {};
    Object.entries(baseInputs).forEach(([k, v]) => {
      const num = Number(v);
      if (!Number.isNaN(num) && Number.isFinite(num)) {
        numeric[k] = num;
      }
    });
    setParams(numeric);
  }, [baseInputs]);

  const toolId = proposedMcpAction?.tool_id || calcArtifact?.artifact_id || '';

  const liveSimulation = useMemo(() => {
    if (
      toolId.includes('soil_nail') ||
      'bond_length_m' in params ||
      'hole_dia_mm' in params ||
      'ult_bond_kpa' in params
    ) {
      const D_m = (params.hole_dia_mm ?? 100) / 1000;
      const tau = params.ult_bond_kpa ?? 120;
      const T_load = params.design_load_kn ?? 140;
      const curLb = params.bond_length_m ?? 6.0;
      const reqFos = 2.0;

      const points: Array<{ x: number; y: number }> = [];
      for (let Lb = 2; Lb <= 14; Lb += 0.5) {
        const T_ult = Math.PI * D_m * tau * Lb;
        const fos = T_ult / Math.max(1, T_load);
        points.push({ x: Lb, y: Number(fos.toFixed(2)) });
      }
      const currentUlt = Math.PI * D_m * tau * curLb;
      const currentFos = currentUlt / Math.max(1, T_load);

      return {
        mode: 'soil_nail',
        chartTitle: 'Geoguide 7 Soil Nail Pull-Out FOS vs. Bond Length (Lb)',
        xLabel: 'Bond Length Lb (m)',
        yLabel: 'Pull-Out Factor of Safety (FOSG)',
        thresholdY: reqFos,
        thresholdLabel: 'Min. Statutory FOSG = 2.0 (Geoguide 7 Table 5.1)',
        currentX: curLb,
        currentY: Number(currentFos.toFixed(2)),
        statusPass: currentFos >= reqFos,
        kpiLabel: `Pull-Out FOS = ${currentFos.toFixed(2)} (Ult. Capacity = ${currentUlt.toFixed(1)} kN)`,
        points,
        sliders: [
          { key: 'bond_length_m', label: 'Bond Length Lb (m)', min: 2, max: 14, step: 0.5, val: curLb },
          { key: 'ult_bond_kpa', label: 'Ult. Bond Stress τ_ult (kPa)', min: 50, max: 300, step: 5, val: tau },
          { key: 'design_load_kn', label: 'Applied Tensile Load T (kN)', min: 40, max: 350, step: 5, val: T_load },
          { key: 'hole_dia_mm', label: 'Drillhole Dia D (mm)', min: 75, max: 200, step: 5, val: params.hole_dia_mm ?? 100 },
        ],
      };
    }

    if (
      toolId.includes('sheet_pile') ||
      toolId.includes('retaining') ||
      'excavation_depth_m' in params ||
      'wall_height_m' in params
    ) {
      const H = params.excavation_depth_m ?? params.wall_height_m ?? 4.5;
      const phi = params.phi_deg ?? 35;
      const curD = params.embedment_depth_m ?? params.base_width_m ?? Number((H * 0.75).toFixed(1));
      const reqFos = 1.5;

      const rad = (phi * Math.PI) / 180;
      const Ka = (1 - Math.sin(rad)) / (1 + Math.sin(rad));
      const Kp = (1 + Math.sin(rad)) / (1 - Math.sin(rad));

      const points: Array<{ x: number; y: number }> = [];
      for (let d = 1.5; d <= 10; d += 0.5) {
        const fos = (Kp * d * d) / Math.max(0.5, Ka * (H + d * 0.45) * (H + d * 0.45)) * 1.35;
        points.push({ x: d, y: Number(Math.min(4.5, fos).toFixed(2)) });
      }
      const curFos = Math.min(
        4.5,
        ((Kp * curD * curD) / Math.max(0.5, Ka * (H + curD * 0.45) * (H + curD * 0.45))) * 1.35
      );

      return {
        mode: 'wall_stability',
        chartTitle: 'Geoguide 1 Toe Kickout / Sliding FOS vs. Embedment / Base (D)',
        xLabel: 'Embedment / Base Dimension D (m)',
        yLabel: 'Factor of Safety (FOS)',
        thresholdY: reqFos,
        thresholdLabel: 'Min. Statutory FOS = 1.50 (Geoguide 1)',
        currentX: curD,
        currentY: Number(curFos.toFixed(2)),
        statusPass: curFos >= reqFos,
        kpiLabel: `Stability FOS = ${curFos.toFixed(2)} (Ka = ${Ka.toFixed(2)}, Kp = ${Kp.toFixed(2)})`,
        points,
        sliders: [
          { key: 'embedment_depth_m', label: 'Embedment / Base D (m)', min: 1.5, max: 10, step: 0.25, val: curD },
          { key: 'excavation_depth_m', label: 'Retained Height H (m)', min: 2.0, max: 10, step: 0.25, val: H },
          { key: 'phi_deg', label: "Soil Friction Angle φ' (°)", min: 24, max: 44, step: 1, val: phi },
        ],
      };
    }

    if (
      toolId.includes('drainage') ||
      toolId.includes('channel') ||
      'width_mm' in params ||
      'gradient_1_in' in params
    ) {
      const B_mm = params.width_mm ?? 600;
      const grad = params.gradient_1_in ?? 100;
      const Q_design = params.design_flow_m3s ?? 0.35;
      const n = 0.015;
      const S = 1 / Math.max(10, grad);

      const calcQ = (w_mm: number) => {
        const b = w_mm / 1000;
        const d = b * 0.85;
        const A = b * d;
        const P = b + 2 * d;
        const R = A / P;
        const V = (1 / n) * Math.pow(R, 2 / 3) * Math.sqrt(S);
        return A * V;
      };

      const points: Array<{ x: number; y: number }> = [];
      for (let w = 300; w <= 1500; w += 75) {
        points.push({ x: w, y: Number(calcQ(w).toFixed(3)) });
      }
      const curCap = calcQ(B_mm);

      return {
        mode: 'drainage_channel',
        chartTitle: 'DSD SDM 2018 U-Channel Capacity Q_cap vs. Channel Width (mm)',
        xLabel: 'U-Channel Width B (mm)',
        yLabel: 'Discharge Capacity Q (m³/s)',
        thresholdY: Q_design,
        thresholdLabel: `Design Peak Runoff Q_design = ${Q_design.toFixed(2)} m³/s`,
        currentX: B_mm,
        currentY: Number(curCap.toFixed(3)),
        statusPass: curCap >= Q_design,
        kpiLabel: `Capacity Q_cap = ${curCap.toFixed(2)} m³/s vs. Demand ${Q_design.toFixed(2)} m³/s`,
        points,
        sliders: [
          { key: 'width_mm', label: 'Channel Width B (mm)', min: 300, max: 1500, step: 75, val: B_mm },
          { key: 'gradient_1_in', label: 'Longitudinal Gradient (1 in X)', min: 20, max: 300, step: 10, val: grad },
          { key: 'design_flow_m3s', label: 'Design Runoff Demand Q (m³/s)', min: 0.05, max: 2.5, step: 0.05, val: Q_design },
        ],
      };
    }

    return null;
  }, [toolId, params]);

  if (!liveSimulation && chartArtifacts.length === 0) return null;

  const renderSvgCurve = (
    points: Array<{ x: number; y: number }>,
    xLabel: string,
    yLabel: string,
    thresholdY?: number,
    thresholdLabel?: string,
    currentX?: number,
    currentY?: number,
    statusPass?: boolean
  ) => {
    if (!points || points.length < 2) return null;
    const W = 560;
    const H = 210;
    const padL = 48;
    const padR = 22;
    const padT = 18;
    const padB = 34;

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    if (typeof thresholdY === 'number') ys.push(thresholdY * 1.15);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = 0;
    const maxY = Math.max(...ys, 1) * 1.12;

    const toSvgX = (x: number) =>
      padL + ((x - minX) / Math.max(1e-6, maxX - minX)) * (W - padL - padR);
    const toSvgY = (y: number) =>
      H - padB - ((y - minY) / Math.max(1e-6, maxY - minY)) * (H - padT - padB);

    const pathD = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toSvgX(p.x).toFixed(1)} ${toSvgY(p.y).toFixed(1)}`)
      .join(' ');

    const areaD = `${pathD} L ${toSvgX(points[points.length - 1].x).toFixed(1)} ${(H - padB).toFixed(1)} L ${toSvgX(points[0].x).toFixed(1)} ${(H - padB).toFixed(1)} Z`;

    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: '220px',
          display: 'block',
          background: 'rgba(7, 11, 20, 0.72)',
          borderRadius: '10px',
          border: '1px solid rgba(148, 163, 184, 0.16)',
        }}
      >
        <defs>
          <linearGradient id="civengCurveArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((frac, idx) => {
          const yVal = minY + (maxY - minY) * frac;
          const sy = toSvgY(yVal);
          return (
            <g key={idx}>
              <line
                x1={padL}
                y1={sy}
                x2={W - padR}
                y2={sy}
                stroke="rgba(148, 163, 184, 0.12)"
                strokeDasharray="3 3"
              />
              <text
                x={padL - 6}
                y={sy + 3}
                textAnchor="end"
                fill="#94a3b8"
                fontSize="10"
                fontFamily="monospace"
              >
                {yVal.toFixed(1)}
              </text>
            </g>
          );
        })}

        <path d={areaD} fill="url(#civengCurveArea)" />
        <path d={pathD} fill="none" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" />

        {typeof thresholdY === 'number' && (
          <g>
            <line
              x1={padL}
              y1={toSvgY(thresholdY)}
              x2={W - padR}
              y2={toSvgY(thresholdY)}
              stroke="#f43f5e"
              strokeWidth="1.5"
              strokeDasharray="5 4"
            />
            {thresholdLabel && (
              <text
                x={W - padR - 4}
                y={toSvgY(thresholdY) - 5}
                textAnchor="end"
                fill="#fda4af"
                fontSize="9.5"
                fontFamily="monospace"
              >
                {thresholdLabel}
              </text>
            )}
          </g>
        )}

        {typeof currentX === 'number' && typeof currentY === 'number' && (
          <g>
            <line
              x1={toSvgX(currentX)}
              y1={toSvgY(currentY)}
              x2={toSvgX(currentX)}
              y2={H - padB}
              stroke={statusPass ? '#10b981' : '#f43f5e'}
              strokeWidth="1.2"
              strokeDasharray="2 2"
            />
            <circle
              cx={toSvgX(currentX)}
              cy={toSvgY(currentY)}
              r="5.5"
              fill={statusPass ? '#10b981' : '#f43f5e'}
              stroke="#ffffff"
              strokeWidth="1.8"
            />
          </g>
        )}

        {points
          .filter((_, idx) => idx % Math.max(1, Math.floor(points.length / 6)) === 0)
          .map((pt, idx) => (
            <text
              key={idx}
              x={toSvgX(pt.x)}
              y={H - 16}
              textAnchor="middle"
              fill="#94a3b8"
              fontSize="10"
              fontFamily="monospace"
            >
              {pt.x}
            </text>
          ))}

        <text
          x={(padL + W - padR) / 2}
          y={H - 3}
          textAnchor="middle"
          fill="#cbd5e1"
          fontSize="10.5"
          fontWeight="600"
        >
          {xLabel}
        </text>
        <text
          x={13}
          y={H / 2}
          textAnchor="middle"
          fill="#cbd5e1"
          fontSize="10.5"
          fontWeight="600"
          transform={`rotate(-90 13 ${H / 2})`}
        >
          {yLabel}
        </text>
      </svg>
    );
  };

  return (
    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '100%' }}>
      {liveSimulation && (
        <div
          className="civeng-ref-card civeng-ref-card-cyan"
          style={{ padding: '14px 16px', maxWidth: '100%', overflow: 'hidden' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '10px',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                className="eng-mono"
                style={{
                  fontSize: '10.5px',
                  fontWeight: 700,
                  color: '#38bdf8',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                📈 Interactive Parameter Sensitivity & Design Curve
              </div>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#f8fafc', marginTop: '2px' }}>
                {liveSimulation.chartTitle}
              </div>
            </div>

            <span
              className="eng-mono"
              style={{
                padding: '4px 10px',
                borderRadius: '999px',
                fontSize: '11.5px',
                fontWeight: 700,
                backgroundColor: liveSimulation.statusPass
                  ? 'rgba(16, 185, 129, 0.18)'
                  : 'rgba(244, 63, 94, 0.18)',
                border: liveSimulation.statusPass
                  ? '1px solid rgba(16, 185, 129, 0.5)'
                  : '1px solid rgba(244, 63, 94, 0.5)',
                color: liveSimulation.statusPass ? '#6ee7b7' : '#fda4af',
              }}
            >
              {liveSimulation.statusPass ? '✓ PASS' : '⚠ BELOW LIMIT'} · {liveSimulation.kpiLabel}
            </span>
          </div>

          {renderSvgCurve(
            liveSimulation.points,
            liveSimulation.xLabel,
            liveSimulation.yLabel,
            liveSimulation.thresholdY,
            liveSimulation.thresholdLabel,
            liveSimulation.currentX,
            liveSimulation.currentY,
            liveSimulation.statusPass
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '10px',
              marginTop: '12px',
            }}
          >
            {liveSimulation.sliders.map((sl) => (
              <div
                key={sl.key}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  border: '1px solid rgba(148, 163, 184, 0.14)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '11.5px',
                    color: '#cbd5e1',
                    marginBottom: '4px',
                  }}
                >
                  <span>{sl.label}</span>
                  <span className="eng-mono" style={{ color: '#7dd3fc', fontWeight: 700 }}>
                    {sl.val}
                  </span>
                </div>
                <input
                  type="range"
                  className="civeng-slider"
                  min={sl.min}
                  max={sl.max}
                  step={sl.step}
                  value={sl.val}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      [sl.key]: parseFloat(e.target.value),
                    }))
                  }
                />
              </div>
            ))}
          </div>

          {proposedMcpAction &&
            (proposedMcpAction.server === 'drainage_mcp' ||
              proposedMcpAction.server === 'borehole_mcp' ||
              proposedMcpAction.ui_launch_url) &&
            onLaunchMcp && (
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => onLaunchMcp(proposedMcpAction)}
                  style={{
                    padding: '7px 14px',
                    borderRadius: '9px',
                    background: 'linear-gradient(135deg, #0284c7, #2563eb)',
                    border: '1px solid rgba(125, 211, 252, 0.45)',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {proposedMcpAction.server === 'drainage_mcp'
                    ? '↗ Open Pre-Populated Network in Drainage Studio'
                    : '↗ Launch Interactive 3D Boreholes & GI Studio'}
                </button>
              </div>
            )}
        </div>
      )}

      {chartArtifacts.map((art) => {
        const series = art.chart_spec?.series;
        const firstSeries = Array.isArray(series) && series.length > 0 ? series[0] : null;
        const rawPts = Array.isArray(firstSeries?.points) ? firstSeries.points : [];
        const pts = rawPts
          .map((p: any) => ({
            x: Number(Array.isArray(p) ? p[0] : p.x),
            y: Number(Array.isArray(p) ? p[1] : p.y),
          }))
          .filter((p: any) => !Number.isNaN(p.x) && !Number.isNaN(p.y));

        if (pts.length < 2) return null;

        return (
          <div
            key={art.artifact_id}
            className="civeng-ref-card civeng-ref-card-purple"
            style={{ padding: '14px 16px', maxWidth: '100%', overflow: 'hidden' }}
          >
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc', marginBottom: '6px' }}>
              📊 {art.title} {art.subtitle ? `— ${art.subtitle}` : ''}
            </div>
            {renderSvgCurve(
              pts,
              art.chart_spec?.x_label || 'X',
              art.chart_spec?.y_label || 'Y'
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Detailed Technical Blueprint Illustrations for the Bento Grid
// ============================================================================
const ToolBlueprintIllustration: React.FC<{ toolId: string }> = ({ toolId }) => {
  if (toolId === 'drainage') {
    return (
      <svg
        width="120"
        height="72"
        viewBox="0 0 120 72"
        fill="none"
        style={{ opacity: 0.85, flexShrink: 0 }}
      >
        <rect x="4" y="8" width="112" height="56" rx="8" fill="#0c1928" stroke="#0284c7" strokeOpacity="0.4" />
        <path d="M22 20V48C22 52.4183 25.5817 56 30 56H54C58.4183 56 62 52.4183 62 48V20" stroke="#38bdf8" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M24 36C32 34 40 38 48 36C54 34.5 58 36 60 36V48C60 51.3137 57.3137 54 54 54H30C26.6863 54 24 51.3137 24 48V36Z" fill="#0284c7" fillOpacity="0.35" />
        <line x1="16" y1="26" x2="104" y2="18" stroke="#22d3ee" strokeWidth="1.2" strokeDasharray="3 3" />
        <circle cx="82" cy="36" r="14" stroke="#38bdf8" strokeWidth="1.5" strokeOpacity="0.6" />
        <path d="M76 36H88M82 30V42" stroke="#38bdf8" strokeWidth="1.2" strokeOpacity="0.7" />
        <text x="72" y="58" fill="#7dd3fc" fontSize="8" fontFamily="monospace">Q=CiA/360</text>
      </svg>
    );
  }

  if (toolId === 'borehole') {
    return (
      <svg
        width="120"
        height="72"
        viewBox="0 0 120 72"
        fill="none"
        style={{ opacity: 0.85, flexShrink: 0 }}
      >
        <rect x="4" y="8" width="112" height="56" rx="8" fill="#1f120b" stroke="#ea580c" strokeOpacity="0.4" />
        <rect x="20" y="16" width="14" height="40" rx="2" stroke="#fb923c" strokeWidth="1.5" />
        <rect x="21" y="17" width="12" height="10" fill="#fdba74" fillOpacity="0.35" />
        <rect x="21" y="28" width="12" height="14" fill="#ea580c" fillOpacity="0.45" />
        <rect x="21" y="43" width="12" height="12" fill="#9a3412" fillOpacity="0.65" />
        <rect x="54" y="14" width="14" height="42" rx="2" stroke="#fb923c" strokeWidth="1.5" />
        <rect x="55" y="15" width="12" height="14" fill="#fdba74" fillOpacity="0.35" />
        <rect x="55" y="30" width="12" height="12" fill="#ea580c" fillOpacity="0.45" />
        <rect x="55" y="43" width="12" height="12" fill="#9a3412" fillOpacity="0.65" />
        <path d="M34 27L54 29M34 42L54 42" stroke="#fdba74" strokeWidth="1.2" strokeDasharray="2 2" />
        <text x="75" y="29" fill="#fed7aa" fontSize="8" fontFamily="monospace">CDG V</text>
        <text x="75" y="42" fill="#fdba74" fontSize="8" fontFamily="monospace">HDG III</text>
        <text x="75" y="54" fill="#fb923c" fontSize="8" fontFamily="monospace">RQD 88%</text>
      </svg>
    );
  }

  if (toolId === 'slope') {
    return (
      <svg width="88" height="52" viewBox="0 0 88 52" fill="none" style={{ opacity: 0.85, flexShrink: 0 }}>
        <path d="M8 44L32 12H80" stroke="#2dd4bf" strokeWidth="2" />
        <line x1="22" y1="26" x2="62" y2="34" stroke="#5eead4" strokeWidth="1.5" />
        <line x1="16" y1="35" x2="56" y2="43" stroke="#5eead4" strokeWidth="1.5" />
        <path d="M14 44C34 42 48 28 52 12" stroke="#f43f5e" strokeWidth="1.3" strokeDasharray="3 2" />
      </svg>
    );
  }

  return (
    <svg width="88" height="52" viewBox="0 0 88 52" fill="none" style={{ opacity: 0.85, flexShrink: 0 }}>
      <path d="M28 10V42H14V46H66V42H38L34 10H28Z" fill="#8b5cf6" fillOpacity="0.3" stroke="#a78bfa" strokeWidth="1.6" />
      <path d="M46 14L68 40H46V14Z" fill="#6366f1" fillOpacity="0.2" stroke="#818cf8" strokeWidth="1.2" strokeDasharray="2 2" />
    </svg>
  );
};

export const Landing: React.FC<LandingProps> = ({
  onEnter,
  onSelectModule,
  userEmail,
  onOpenAuth,
  onSignOut,
}) => {
  // Responsive Breakpoints
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  const isMobile = viewportWidth < 768;
  const isCompact = viewportWidth < 1160;

  // Sidebar & Layout state
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1160 : false
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);
  const [searchChatsQuery, setSearchChatsQuery] = useState<string>('');
  const [manualFilterQuery, setManualFilterQuery] = useState<string>('');
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);

  // Dynamic Ingested Manuals list from /api/kb/documents
  const [ingestedManuals, setIngestedManuals] = useState<IngestedDocumentItem[]>(
    DEFAULT_FALLBACK_MANUALS
  );

  // Chat sessions state (10-day local retention)
  const [sessions, setSessions] = useState<ChatSession[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed: ChatSession[] = JSON.parse(raw);
      const tenDaysAgo = Date.now() - 10 * 24 * 60 * 60 * 1000;
      return parsed.filter((s) => s.updatedAt >= tenDaysAgo);
    } catch {
      return [];
    }
  });
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // Input, Loading Stage & Progressive Text Preview State
  const [inputPrompt, setInputPrompt] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingStageIndex, setLoadingStageIndex] = useState<number>(0);
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [streamedCharCount, setStreamedCharCount] = useState<number>(0);
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);

  // Right-hand PDF / Clause Drawer state (Strictly User-Invoked Only)
  const [activeCitation, setActiveCitation] = useState<CitationItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<'pdf' | 'clause'>('pdf');
  const [pdfPage, setPdfPage] = useState<number>(1);
  const [pdfTotalPages, setPdfTotalPages] = useState<number | null>(null);
  const [drawerExpanded, setDrawerExpanded] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatScrollContainerRef = useRef<HTMLDivElement>(null);
  const unifiedFileInputRef = useRef<HTMLInputElement>(null);
  const heroTextareaRef = useRef<HTMLTextAreaElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const dragCounterRef = useRef<number>(0);

  const activeSession = sessions.find((s) => s.id === activeSessionId) || null;

  // Detect whether current device is a phone / coarse touch screen
  const isPhoneInputDevice = () => {
    if (isMobile) return true;
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(pointer: coarse)').matches && window.innerWidth < 1024;
    }
    return false;
  };

  // Cycle through thought process stages while waiting for backend pipeline
  useEffect(() => {
    if (!isLoading) {
      setLoadingStageIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setLoadingStageIndex((prev) =>
        prev < LOADING_THOUGHT_STAGES.length - 1 ? prev + 1 : prev
      );
    }, 1800);
    return () => clearInterval(timer);
  }, [isLoading]);

  // Progressive text generation preview effect
  useEffect(() => {
    if (!streamingMsgId || !activeSession) return;
    const targetMsg = activeSession.messages.find((m) => m.id === streamingMsgId);
    if (!targetMsg) {
      setStreamingMsgId(null);
      return;
    }

    const totalLen = targetMsg.content.length;
    if (streamedCharCount >= totalLen) {
      setStreamingMsgId(null);
      return;
    }

    const step = Math.max(18, Math.ceil(totalLen / 42));
    const timer = window.setTimeout(() => {
      setStreamedCharCount((prev) => Math.min(totalLen, prev + step));
    }, 24);

    return () => clearTimeout(timer);
  }, [streamingMsgId, streamedCharCount, activeSession]);

  // Auto-resize helper for both textareas
  const adjustTextareaHeight = (el: HTMLTextAreaElement | null, minHeight = 26, maxHeight = 180) => {
    if (!el) return;
    el.style.height = 'auto';
    const nextHeight = Math.min(maxHeight, Math.max(minHeight, el.scrollHeight));
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    adjustTextareaHeight(heroTextareaRef.current, 60, 190);
    adjustTextareaHeight(chatTextareaRef.current, 26, 170);
  }, [inputPrompt, activeSessionId]);

  // Responsive listener
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setViewportWidth(w);
      if (w >= 768) {
        setMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all ingested manuals from PostgreSQL on mount
  useEffect(() => {
    let isMounted = true;
    fetch('/api/kb/documents')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (isMounted && Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort(
            (a, b) => (Number(b.total_clauses) || 0) - (Number(a.total_clauses) || 0)
          );
          setIngestedManuals(sorted);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  // Persist sessions
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    } catch {}
  }, [sessions]);

  // Safely scroll ONLY the inner chat thread container (never the root window/stage)
  useEffect(() => {
    const container = chatScrollContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    });
  }, [activeSession?.id, activeSession?.messages.length, isLoading]);

  // ==========================================================================
  // Unified Multimodal File Upload (/api/kb/upload) & Drag-and-Drop Handlers
  // ==========================================================================
  const processFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList).slice(0, 6);
    if (files.length === 0) return;

    const newItems: ChatAttachment[] = [];

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      let previewUrl: string | undefined;
      let extractedText: string | undefined;

      if (isImage) {
        previewUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.readAsDataURL(file);
        });
      } else if (
        file.type.includes('text') ||
        /\.(txt|csv|md|json|ags|log|py|sql)$/i.test(file.name)
      ) {
        extractedText = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || '').slice(0, 12000));
          reader.readAsText(file);
        });
      }

      newItems.push({
        id,
        name: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
        kind: isImage ? 'image' : 'document',
        previewUrl,
        imageDataUrl: previewUrl,
        extractedText,
        status: 'uploading',
      });
    }

    setPendingAttachments((prev) => [...prev, ...newItems]);

    const activeToken = localStorage.getItem('civeng_bh_auth_token_v1');
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const res = await fetch('/api/kb/upload', {
        method: 'POST',
        headers: activeToken ? { Authorization: `Bearer ${activeToken}` } : undefined,
        body: formData,
      });
      if (res.ok) {
        const uploadedList = await res.json();
        if (Array.isArray(uploadedList)) {
          setPendingAttachments((prev) =>
            prev.map((att) => {
              const matched = uploadedList.find((u: any) => u.filename === att.name);
              if (!matched) return { ...att, status: 'ready' };
              return {
                ...att,
                kind: matched.kind || att.kind,
                extractedText: matched.extracted_text || att.extractedText,
                imageDataUrl: matched.image_data_url || att.imageDataUrl,
                extraImageUrls: matched.extra_image_urls || undefined,
                previewUrl: matched.image_data_url || att.previewUrl,
                status: 'ready',
              };
            })
          );
          return;
        }
      }
    } catch {
      // Fallback to client-parsed data if offline
    }

    setPendingAttachments((prev) =>
      prev.map((att) => ({ ...att, status: 'ready' }))
    );
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handlePasteClipboard = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // ==========================================================================
  // Tool & Drawer Navigation Handlers
  // ==========================================================================
  const handleLaunchTool = (toolId: string) => {
    const activeToken = localStorage.getItem('civeng_bh_auth_token_v1');
    if (toolId === 'borehole' || toolId === 'boreholes') {
      if (!activeToken) {
        onOpenAuth?.(
          'Unlock Chartered Workspace to launch the 3D Boreholes & GI BIM Studio.',
          () => {
            window.location.href = '/boreholes/';
          }
        );
        return;
      }
      window.location.href = '/boreholes/';
      return;
    }
    if (toolId === 'drainage') {
      if (onSelectModule) onSelectModule('drainage');
      else if (onEnter) onEnter('drainage');
      return;
    }
    if (toolId === 'slope') {
      handleSendPrompt(
        'Walk me through a Geoguide 7 Soil Nail slope upgrading check with bond length Lb = 6.0 m, drillhole diameter D = 100 mm, ultimate bond stress τ_ult = 120 kPa, and design tensile load T = 140 kN.'
      );
      return;
    }
    if (toolId === 'retaining' || toolId === 'retaining-wall') {
      handleSendPrompt(
        "Run a Geoguide 1 & ELS Sheet Pile / Retaining Wall stability check for an excavation depth H = 4.5 m, embedment depth D = 3.5 m, and CDG soil friction angle φ' = 35°."
      );
    }
  };

  const handleLaunchMcpAction = (mcp: ProposedMcpAction) => {
    if (mcp.server === 'drainage_mcp') {
      if (mcp.editor_v5_project) {
        try {
          localStorage.setItem(
            'civeng_drainage_mcp_handoff_v1',
            JSON.stringify(mcp.editor_v5_project)
          );
        } catch (e) {
          console.warn('Could not save MCP drainage handoff to localStorage:', e);
        }
      }
      handleLaunchTool('drainage');
      return;
    }

    if (mcp.server === 'borehole_mcp') {
      const targetUrl = mcp.ui_launch_url || '/boreholes/';
      window.location.href = targetUrl;
      return;
    }

    if (mcp.ui_launch_url) {
      window.location.href = mcp.ui_launch_url;
    }
  };

  const openManualInDrawer = (doc: IngestedDocumentItem) => {
    const manualCit: CitationItem = {
      chunk_id: `manual-${doc.doc_code}`,
      doc_slug: doc.doc_code,
      doc_title: doc.doc_title,
      authority: doc.domain.toUpperCase(),
      clause_id: 'Full Manual',
      clause_title: `${doc.total_clauses} Indexed Clauses`,
      page_number: 1,
      chunk_type: 'manual',
      content: `${doc.doc_title} (${doc.doc_code}) — Domain: ${doc.domain}. Contains ${doc.total_clauses} indexed clauses/tables in the Knowledge Base.`,
      pdf_url: normalizePdfUrl(doc.pdf_url, 1),
      score: 1.0,
    };
    openCitationDrawer(manualCit, 'pdf');
    if (isMobile) setMobileSidebarOpen(false);
  };

  // Strictly User-Invoked Drawer Opener
  const openCitationDrawer = (
    cit: CitationItem,
    preferredTab: 'pdf' | 'clause' = 'pdf'
  ) => {
    const activeToken = localStorage.getItem('civeng_bh_auth_token_v1');
    if (!activeToken) {
      onOpenAuth?.(
        `Unlock Chartered Workspace to view ${cit.doc_slug || cit.doc_title} (Page ${cit.page_number || 1}).`,
        () => openCitationDrawer(cit, preferredTab)
      );
      return;
    }

    const targetPage = cit.page_number && cit.page_number > 0 ? cit.page_number : 1;
    setPdfTotalPages(null);
    setActiveCitation({
      ...cit,
      page_number: targetPage,
      pdf_url: normalizePdfUrl(cit.pdf_url, targetPage),
    });
    setPdfPage(targetPage);
    setDrawerTab(preferredTab);
    setDrawerOpen(true);

    if (window.innerWidth < 1440 && !isMobile) {
      setSidebarCollapsed(true);
    }
  };

  const handleStartNewChat = () => {
    setActiveSessionId(null);
    setDrawerOpen(false);
    setStreamingMsgId(null);
    setInputPrompt('');
    setPendingAttachments([]);
    if (isMobile) setMobileSidebarOpen(false);
  };

  const handleClearHistory = () => {
    setSessions([]);
    setActiveSessionId(null);
    setActiveCitation(null);
    setDrawerOpen(false);
    setStreamingMsgId(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // ==========================================================================
  // Submit Prompt + Unified Attachments to FastAPI (/api/kb/ask)
  // ==========================================================================
  const handleSendPrompt = async (overrideText?: string) => {
    const rawQuery = (overrideText ?? inputPrompt).trim();
    if ((!rawQuery && pendingAttachments.length === 0) || isLoading) return;

    const activeToken = localStorage.getItem('civeng_bh_auth_token_v1');
    if (!activeToken) {
      onOpenAuth?.(
        'Unlock Chartered Workspace to run the Autonomous Engineering Copilot.',
        () => handleSendPrompt(rawQuery)
      );
      return;
    }

    const currentAttachments = [...pendingAttachments];
    setInputPrompt('');
    setPendingAttachments([]);

    if (heroTextareaRef.current) heroTextareaRef.current.style.height = 'auto';
    if (chatTextareaRef.current) chatTextareaRef.current.style.height = 'auto';

    const synthesizedQuestion =
      rawQuery ||
      `Analyze the attached ${currentAttachments.map((a) => a.name).join(', ')} against HK engineering standards.`;

    const priorMessages = activeSession ? activeSession.messages : [];
    const historyPayload = priorMessages.slice(-6).map((m) => ({
      role: m.role,
      content: sanitizeAssistantMarkdown(m.content),
    }));

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: synthesizedQuestion,
      timestamp: formatTime(),
      attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
    };

    let targetSessionId = activeSessionId;
    if (!targetSessionId) {
      const newSession: ChatSession = {
        id: `chat-${Date.now()}`,
        title: synthesizedQuestion.slice(0, 46),
        updatedAt: Date.now(),
        lastTierExecuted: 2,
        messages: [userMsg],
      };
      setSessions((prev) => [newSession, ...prev]);
      setActiveSessionId(newSession.id);
      targetSessionId = newSession.id;
    } else {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === targetSessionId
            ? {
                ...s,
                updatedAt: Date.now(),
                messages: [...s.messages, userMsg],
              }
            : s
        )
      );
    }

    setIsLoading(true);
    setLoadingStageIndex(0);

    try {
      const res = await fetch('/api/kb/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${activeToken}`,
        },
        body: JSON.stringify({
          question: synthesizedQuestion,
          top_k: 6,
          session_id: targetSessionId,
          history: historyPayload,
          attachments: currentAttachments.map((a) => ({
            id: a.id,
            filename: a.name,
            mime_type: a.mimeType,
            kind: a.kind,
            extracted_text: a.extractedText || null,
            image_data_url: a.imageDataUrl || a.previewUrl || null,
            extra_image_urls: a.extraImageUrls || null,
          })),
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }

      const data = await res.json();
      const rawAssistantContent =
        data.answer_markdown ||
        data.answer ||
        'No response returned from Knowledge Base.';
      const cleanContent = sanitizeAssistantMarkdown(rawAssistantContent);

      const isMetaOrWeights =
        data.epistemic_route === 'meta' ||
        data.epistemic_route === 'internal_weights' ||
        data.query_category === 'meta_conversational' ||
        /^(?:what\s+can\s+you\s+do|who\s+are\s+you|hi|hello|hey|thanks)/i.test(
          synthesizedQuestion.trim()
        );

      const citations = isMetaOrWeights
        ? []
        : normalizeBackendCitations(data.citations || []).filter((c) => Boolean(c.pdf_url));

      const executedTier: ExecutedTierNumber =
        data.tier_executed === 1 || data.tier_executed === 3 ? data.tier_executed : 2;

      const thoughtSteps: string[] = [];
      if (data.epistemic_route) {
        thoughtSteps.push(
          `Selected route "${data.epistemic_route}" with depth "${data.query_depth || 'L2_clause_check'}".`
        );
      }
      if (citations.length > 0) {
        const uniqueDocs = Array.from(new Set(citations.map((c) => c.doc_slug))).join(', ');
        thoughtSteps.push(
          `Grounded against ${citations.length} statutory clause(s) from ${uniqueDocs}.`
        );
      }
      if (data.proposed_mcp_action?.tool_name) {
        thoughtSteps.push(
          `Executed deterministic calculator: ${data.proposed_mcp_action.tool_name}.`
        );
      }

      const assistantMsgId = `a-${Date.now()}`;
      const assistantMsg: ChatMessage = {
        id: assistantMsgId,
        role: 'assistant',
        content: cleanContent,
        timestamp: formatTime(),
        tierExecuted: executedTier,
        epistemicRoute: data.epistemic_route || 'hybrid_kb',
        queryDepth: data.query_depth || 'L2_clause_check',
        queryCategory: data.query_category || undefined,
        modelUsed: data.model_used || TIER_VISUALS[executedTier].modelName,
        synthesisRole: data.synthesis_role || undefined,
        configVersion: data.config_version ?? undefined,
        routingReason: data.routing_reason || undefined,
        thoughtSummary: thoughtSteps,
        confidenceScore:
          typeof data.confidence_score === 'number' ? data.confidence_score : undefined,
        expandedQuery: data.expanded_query || undefined,
        citations,
        proposedMcpAction: data.proposed_mcp_action || null,
        interactiveArtifacts: Array.isArray(data.interactive_artifacts)
          ? data.interactive_artifacts
          : [],
        workflowBlueprint: data.workflow_blueprint || null,
        sourceChecks: data.source_checks || null,
        sourceCheckVerdict: data.source_check_verdict || null,
      };

      // Start progressive text generation preview
      setStreamedCharCount(Math.min(cleanContent.length, 48));
      setStreamingMsgId(assistantMsgId);

      setSessions((prev) =>
        prev.map((s) =>
          s.id === targetSessionId
            ? {
                ...s,
                id: data.session_id || s.id,
                updatedAt: Date.now(),
                lastTierExecuted: executedTier,
                designState: data.active_design_state || s.designState,
                messages: [...s.messages, assistantMsg],
              }
            : s
        )
      );
      if (data.session_id && data.session_id !== targetSessionId) {
        setActiveSessionId(data.session_id);
      }

      if (data.close_drawer === true || isMetaOrWeights) {
        setDrawerOpen(false);
      } else if (citations.length > 0) {
        const primaryVisId = data.primary_visual_chunk_id;
        const targetCit =
          (primaryVisId && citations.find((c) => Number(c.chunk_id) === Number(primaryVisId))) ||
          citations[0];
        const targetPage = targetCit.page_number > 0 ? targetCit.page_number : 1;
        setActiveCitation({
          ...targetCit,
          page_number: targetPage,
          pdf_url: normalizePdfUrl(targetCit.pdf_url, targetPage),
        });
        setPdfPage(targetPage);
        setDrawerTab('pdf');

        const userExplicitlyAskedToOpenPdf =
          /\b(?:open\s+(?:the\s+)?pdf|show\s+me\s+(?:the\s+)?pdf|view\s+(?:the\s+)?pdf|display\s+(?:the\s+)?pdf)\b/i.test(
            synthesizedQuestion
          );
        if (userExplicitlyAskedToOpenPdf && !isMobile) {
          setDrawerOpen(true);
        }
      }
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: `Knowledge Base API error (${
          err?.message || 'Connection failed'
        }). Verify that \`civeng-api.service\` is running on port 8000.`,
        timestamp: formatTime(),
        tierExecuted: 1,
      };
      setSessions((prev) =>
        prev.map((s) =>
          s.id === targetSessionId
            ? { ...s, updatedAt: Date.now(), messages: [...s.messages, errorMsg] }
            : s
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyMessage = (msgId: string, text: string) => {
    navigator.clipboard?.writeText(sanitizeAssistantMarkdown(text));
    setCopiedMsgId(msgId);
    setTimeout(() => setCopiedMsgId(null), 1800);
  };

  // ==========================================================================
  // Rich Inline Token Renderer (**bold**, $LaTeX$, `code`, & Small Faded [Citations])
  // Fixed Issue #4: Quote references are rendered as compact, faded buttons
  // ==========================================================================
  const renderInlineTokens = (
    text: string,
    citations?: CitationItem[]
  ): React.ReactNode => {
    const healedText = healCorruptedLatexEscapes(text);
    const tokenRegex =
      /(!\[[^\]]*\]\([^)]+\)|\*\*.*?\*\*|\$[^$\n]+\$|`[^`\n]+`|\[\d+\]|\([^\)]*(?:GEOGUIDE|Clause|Cl\.|Table|Figure|Fig\.|Section|SDM|GEO|TGN|BD|COP|TPDM|Cap\.|PNAP|CS1)[^\)]*\)|\[[^\]]*(?:Clause|Cl\.|Table|Figure|Fig\.|Section|Appendix|p\.\s*\d+|SDM|GEO|Geoguide|TGN|BD|COP|TPDM|Cap\.|PNAP|CS1|External Web)[^\]]*\])/gi;

    const parts = healedText.split(tokenRegex);
    return parts.map((part, idx) => {
      if (!part) return null;

      // 0. Inline Markdown Image ![alt](url)
      const imgMatch = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) {
        return (
          <figure
            key={idx}
            style={{
              margin: '12px 0',
              padding: '10px',
              borderRadius: '12px',
              backgroundColor: 'rgba(10, 15, 26, 0.82)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            <img
              src={imgMatch[2]}
              alt={imgMatch[1] || 'Engineering Figure'}
              style={{ maxWidth: '100%', borderRadius: '8px', display: 'block' }}
            />
            {imgMatch[1] && (
              <figcaption
                className="eng-mono"
                style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}
              >
                {imgMatch[1]}
              </figcaption>
            )}
          </figure>
        );
      }

      // 1. Bold text (**...**)
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        const innerBold = part.slice(2, -2);
        return (
          <strong key={idx} style={{ color: '#f8fafc', fontWeight: 700 }}>
            {renderInlineTokens(innerBold, citations)}
          </strong>
        );
      }

      // 2. Inline Math ($c'$, $\phi'$, $T$, $T_p$)
      if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
        return (
          <span key={idx} className="eng-math-inline">
            {renderLatexNode(part.slice(1, -1), false)}
          </span>
        );
      }

      // 3. Inline Code (`code`)
      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        const codeInner = part.slice(1, -1).trim();
        if (
          /^[a-zA-Z0-9\\]+_[a-zA-Z0-9{}]+$/.test(codeInner) ||
          /[=<>]|\\frac|\\phi|\\tau|\\gamma|\\sigma/.test(codeInner)
        ) {
          return (
            <span key={idx} className="eng-math-inline">
              {renderLatexNode(codeInner, false)}
            </span>
          );
        }
        return (
          <code
            key={idx}
            className="eng-mono"
            style={{
              padding: '1px 6px',
              borderRadius: '5px',
              backgroundColor: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.22)',
              color: '#e0f2fe',
              fontSize: '0.9em',
            }}
          >
            {codeInner}
          </code>
        );
      }

      // 4. Numeric Citation [1] or Raw Chunk ID [4039] -> Small Faded Button
      const citeMatch = part.match(/^\[(\d+)\]$/);
      if (citeMatch && citations && citations.length > 0) {
        const rawNum = parseInt(citeMatch[1], 10);
        const byChunkId = citations.find((c) => Number(c.chunk_id) === rawNum);
        const targetCit =
          byChunkId || citations[Math.max(0, rawNum - 1)] || citations[0];

        const shortDoc = targetCit.doc_slug || 'Ref';
        const shortClause = targetCit.clause_number || targetCit.clause_id;
        const pillLabel = byChunkId
          ? `${shortDoc}${shortClause ? ` Cl.${shortClause}` : ''} p.${targetCit.page_number}`
          : `[${rawNum}] p.${targetCit.page_number}`;

        return (
          <button
            key={idx}
            type="button"
            onClick={() => openCitationDrawer(targetCit, 'pdf')}
            title={`Open ${targetCit.doc_title} (Page ${targetCit.page_number}) in PDF Drawer`}
            className="eng-mono civeng-inline-cite"
          >
            {pillLabel}
          </button>
        );
      }

      // 5. Named Statutory Citation Pill -> Small Faded Button
      const clauseMatch = part.match(
        /^[\[\(]([^\]\)]*(?:Clause|Cl\.|Table|Figure|Fig\.|Section|Appendix|p\.\s*(\d+)|SDM|GEO|Geoguide|TGN|BD|COP|TPDM|Cap\.|PNAP|CS1|External Web)[^\]\)]*)[\]\)]$/i
      );
      if (clauseMatch) {
        let innerText = clauseMatch[1]
          .replace(/^(?:Doc\s*Code|CHUNK_ID)\s*:\s*/i, '')
          .trim();

        if (/External Web/i.test(innerText)) {
          return (
            <span
              key={idx}
              className="eng-mono civeng-inline-cite"
              style={{ cursor: 'default' }}
            >
              {innerText}
            </span>
          );
        }

        if (citations && citations.length > 0) {
          const leadingChunkIdMatch = innerText.match(/^(\d{2,6})\s*,\s*(.*)$/);
          const leadingChunkId = leadingChunkIdMatch
            ? parseInt(leadingChunkIdMatch[1], 10)
            : null;
          const pageMatch = innerText.match(/p\.\s*(\d+)/i);
          const parsedPage = pageMatch ? parseInt(pageMatch[1], 10) : null;

          const matchedCit =
            (leadingChunkId &&
              citations.find((c) => Number(c.chunk_id) === leadingChunkId)) ||
            (parsedPage && citations.find((c) => c.page_number === parsedPage)) ||
            citations.find((c) =>
              innerText.toLowerCase().includes((c.doc_slug || '').toLowerCase())
            ) ||
            citations.find(
              (c) =>
                c.clause_id &&
                innerText.toLowerCase().includes(c.clause_id.toLowerCase())
            ) ||
            citations[0];

          if (leadingChunkIdMatch && matchedCit?.doc_slug) {
            innerText = `${matchedCit.doc_slug}, ${leadingChunkIdMatch[2]}`;
          }
          if (/^Doc[_\s]*Code\b/i.test(innerText) && matchedCit?.doc_slug) {
            innerText = innerText.replace(/^Doc[_\s]*Code\s*[:,]?\s*/i, `${matchedCit.doc_slug}, `);
          }

          return (
            <button
              key={idx}
              type="button"
              onClick={() => openCitationDrawer(matchedCit, 'pdf')}
              title={`Open ${matchedCit.doc_title} (p.${matchedCit.page_number}) in PDF Drawer`}
              className="eng-mono civeng-inline-cite"
            >
              {innerText}
            </button>
          );
        }
      }

      return <span key={idx}>{part}</span>;
    });
  };

  // ==========================================================================
  // Full Block-Level Engineering Markdown Renderer
  // Fixed Issue #6: Restored explicit bullet dots (`•`) on all bulleted lists
  // ==========================================================================
  const renderFormattedText = (rawText: string, citations?: CitationItem[]) => {
    const clean = sanitizeAssistantMarkdown(rawText);
    const lines = clean.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const rawLine = lines[i];
      const trimmed = rawLine.trim();

      if (!trimmed) {
        i += 1;
        continue;
      }

      // 1. Horizontal Divider
      if (/^---+$/.test(trimmed)) {
        elements.push(
          <hr
            key={`hr-${i}`}
            style={{
              border: 'none',
              borderTop: '1px solid rgba(148, 163, 184, 0.16)',
              margin: '16px 0',
            }}
          />
        );
        i += 1;
        continue;
      }

      // 2. Display Math Block ($$ ... $$)
      if (trimmed.startsWith('$$')) {
        const mathLines: string[] = [];
        if (trimmed.endsWith('$$') && trimmed.length > 4) {
          mathLines.push(trimmed.slice(2, -2));
          i += 1;
        } else {
          mathLines.push(trimmed.slice(2));
          i += 1;
          while (i < lines.length && !lines[i].trim().endsWith('$$')) {
            mathLines.push(lines[i]);
            i += 1;
          }
          if (i < lines.length) {
            mathLines.push(lines[i].trim().slice(0, -2));
            i += 1;
          }
        }
        elements.push(
          <div key={`math-${i}`} className="eng-math-block">
            {renderLatexNode(mathLines.join(' '), true)}
          </div>
        );
        continue;
      }

      // 3. Markdown Table Block (`| Col 1 | Col 2 |`)
      if (trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.split('|').length >= 3) {
        const tableLines: string[] = [];
        while (
          i < lines.length &&
          lines[i].trim().startsWith('|') &&
          lines[i].trim().includes('|')
        ) {
          tableLines.push(lines[i].trim());
          i += 1;
        }

        if (tableLines.length >= 2) {
          const parseRow = (r: string) =>
            r
              .replace(/^\|/, '')
              .replace(/\|$/, '')
              .split('|')
              .map((c) => c.trim());

          const headers = parseRow(tableLines[0]);
          const isSeparator = (r: string) => /^[\|\s:\-]+$/.test(r);
          const bodyStartIndex = isSeparator(tableLines[1]) ? 2 : 1;
          const bodyRows = tableLines
            .slice(bodyStartIndex)
            .filter((r) => !isSeparator(r))
            .map(parseRow);

          elements.push(
            <div key={`tbl-${i}`} className="eng-table-wrap">
              <table className="eng-table">
                <thead>
                  <tr>
                    {headers.map((h, hIdx) => (
                      <th key={hIdx}>{renderInlineTokens(h, citations)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      {headers.map((_, cIdx) => (
                        <td key={cIdx}>
                          {renderInlineTokens(row[cIdx] ?? '', citations)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          continue;
        }
      }

      // 4. Headings (##, ###, ####)
      const h2Match = trimmed.match(/^#{1,2}\s+(.*)$/);
      if (h2Match) {
        elements.push(
          <h2 key={`h2-${i}`}>{renderInlineTokens(h2Match[1], citations)}</h2>
        );
        i += 1;
        continue;
      }

      const h3Match = trimmed.match(/^###\s+(.*)$/);
      if (h3Match) {
        elements.push(
          <h3 key={`h3-${i}`}>{renderInlineTokens(h3Match[1], citations)}</h3>
        );
        i += 1;
        continue;
      }

      const h4Match = trimmed.match(/^####\s+(.*)$/);
      if (h4Match) {
        elements.push(
          <h4 key={`h4-${i}`}>{renderInlineTokens(h4Match[1], citations)}</h4>
        );
        i += 1;
        continue;
      }

      // 5. Step Callout Header
      const stepMatch = trimmed.match(/^(?:\*\*)?(Step\s+\d+\s*[—–\-:]\s*.*?)(\*\*)?$/i);
      if (stepMatch && trimmed.length < 130) {
        const cleanStep = trimmed.replace(/\*\*/g, '');
        elements.push(
          <div key={`step-${i}`} className="eng-step-card">
            {renderInlineTokens(cleanStep, citations)}
          </div>
        );
        i += 1;
        continue;
      }

      // 5b. Blockquote Callout (`> Note: ...`)
      if (/^>\s*/.test(trimmed)) {
        const quoteLines: string[] = [];
        while (i < lines.length && /^>\s*/.test(lines[i].trim())) {
          quoteLines.push(lines[i].trim().replace(/^>\s*/, ''));
          i += 1;
        }
        elements.push(
          <div
            key={`bq-${i}`}
            style={{
              margin: '10px 0 14px 0',
              padding: '10px 14px',
              borderRadius: '0 10px 10px 0',
              borderLeft: '3px solid #38bdf8',
              backgroundColor: 'rgba(56, 189, 248, 0.08)',
              color: '#cbd5e1',
              fontSize: '13.5px',
              lineHeight: 1.6,
            }}
          >
            {renderInlineTokens(quoteLines.join(' '), citations)}
          </div>
        );
        continue;
      }

      // 6. Bulleted List (`- ` or `* ` or `• `) — Explicit visible bullet dot
      if (/^[-*•]\s+/.test(trimmed)) {
        const items: Array<{ text: string; indent: number }> = [];
        while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
          const leadingSpaces = (lines[i].match(/^\s*/)?.[0]?.length || 0);
          items.push({
            text: lines[i].trim().replace(/^[-*•]\s+/, ''),
            indent: leadingSpaces >= 2 ? 1 : 0,
          });
          i += 1;
        }
        elements.push(
          <ul
            key={`ul-${i}`}
            style={{
              margin: '6px 0 14px 0',
              paddingLeft: '4px',
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {items.map((it, idx) => (
              <li
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  paddingLeft: it.indent ? '16px' : '2px',
                  color: '#e2e8f0',
                  lineHeight: 1.65,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    color: '#38bdf8',
                    fontSize: it.indent ? '12px' : '15px',
                    lineHeight: 1.5,
                    flexShrink: 0,
                    userSelect: 'none',
                  }}
                >
                  {it.indent ? '◦' : '•'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renderInlineTokens(it.text, citations)}
                </div>
              </li>
            ))}
          </ul>
        );
        continue;
      }

      // 7. Numbered List (`1. `, `2. `)
      if (/^\d+\.\s+/.test(trimmed)) {
        const items: Array<{ num: string; body: string }> = [];
        while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
          const m = lines[i].trim().match(/^(\d+)\.\s+(.*)$/);
          if (m) items.push({ num: m[1], body: m[2] });
          i += 1;
        }
        elements.push(
          <div
            key={`ol-${i}`}
            style={{
              margin: '10px 0 14px 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            {items.map((it, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}
              >
                <span
                  className="eng-mono"
                  style={{
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '7px',
                    backgroundColor: 'rgba(56, 189, 248, 0.16)',
                    border: '1px solid rgba(56, 189, 248, 0.42)',
                    color: '#7dd3fc',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px',
                    flexShrink: 0,
                  }}
                >
                  {it.num}
                </span>
                <div style={{ flex: 1, minWidth: 0, lineHeight: 1.65 }}>
                  {renderInlineTokens(it.body, citations)}
                </div>
              </div>
            ))}
          </div>
        );
        continue;
      }

      // 8. Standard Paragraph
      elements.push(
        <p key={`p-${i}`}>{renderInlineTokens(trimmed, citations)}</p>
      );
      i += 1;
    }

    const citedFigures = (citations || []).filter((c) => Boolean(c.figure_url)).slice(0, 2);

    return (
      <div className="eng-prose">
        {elements}
        {citedFigures.length > 0 && (
          <div
            style={{
              marginTop: '14px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px',
              maxWidth: '100%',
            }}
          >
            {citedFigures.map((fig) => (
              <div
                key={`fig-${fig.chunk_id}`}
                onClick={() => openCitationDrawer(fig, 'pdf')}
                className="civeng-ref-card"
                style={{ padding: '10px', cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
              >
                <img
                  src={fig.figure_url!}
                  alt={fig.figure_caption || fig.doc_slug}
                  style={{
                    width: '100%',
                    maxHeight: '220px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    backgroundColor: '#090d16',
                  }}
                />
                <div
                  style={{
                    fontSize: '11.5px',
                    color: '#cbd5e1',
                    marginTop: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '6px',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fig.figure_caption || `${fig.doc_slug} Figure`}
                  </span>
                  <span className="eng-mono" style={{ color: '#38bdf8', flexShrink: 0 }}>
                    p.{fig.page_number} ↗
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const filteredSessions = sessions.filter((s) =>
    s.title.toLowerCase().includes(searchChatsQuery.toLowerCase())
  );

  const filteredManuals = ingestedManuals.filter((m) => {
    if (!manualFilterQuery.trim()) return true;
    const q = manualFilterQuery.toLowerCase();
    return (
      m.doc_code.toLowerCase().includes(q) ||
      m.doc_title.toLowerCase().includes(q) ||
      m.domain.toLowerCase().includes(q)
    );
  });

  const getActivePdfSrc = () => {
    if (!activeCitation) return '';
    return normalizePdfUrl(activeCitation.pdf_url, pdfPage);
  };

  const isSidebarExpanded = isMobile ? true : !sidebarCollapsed;

  // Shared Attachment Staging Strip Component
  const renderPendingAttachmentsStrip = () => {
    if (pendingAttachments.length === 0) return null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          paddingBottom: '10px',
          marginBottom: '8px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
        }}
      >
        {pendingAttachments.map((att) => (
          <div
            key={att.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '5px 10px',
              borderRadius: '10px',
              backgroundColor: 'rgba(20, 28, 46, 0.85)',
              border: '1px solid rgba(56, 189, 248, 0.4)',
              fontSize: '12px',
              color: '#e2e8f0',
              maxWidth: '230px',
            }}
          >
            {att.kind === 'image' && att.previewUrl ? (
              <img
                src={att.previewUrl}
                alt={att.name}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '5px',
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <span style={{ fontSize: '14px', flexShrink: 0 }}>📄</span>
            )}
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 500,
                }}
              >
                {att.name}
              </div>
              <div className="eng-mono" style={{ fontSize: '10px', color: '#94a3b8' }}>
                {att.status === 'uploading' ? 'Parsing...' : formatFileSize(att.size)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeAttachment(att.id)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                padding: '2px',
                fontSize: '12px',
              }}
              title="Remove attachment"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="civeng-galaxy-stage"
      style={{
        display: 'flex',
        height: '100dvh',
        width: '100vw',
        overflow: 'hidden',
        position: 'relative',
        padding: isMobile ? '0px' : '12px',
        gap: isMobile ? '0px' : '14px',
      }}
    >
      {/* Subtle Interactive Galaxy Starfield & Shooting Meteor Layer */}
      <InteractiveGalaxyCanvas />

      {/* Single Unified File Input */}
      <input
        ref={unifiedFileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.txt,.csv,.ags,.json,.md,.xlsx,.py,.sql"
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) processFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Full-Window Drag & Drop Overlay */}
      {isDraggingOver && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            backgroundColor: 'rgba(5, 7, 14, 0.84)',
            backdropFilter: 'blur(14px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            pointerEvents: 'none',
          }}
        >
          <div
            className="civeng-floating-pane-glow"
            style={{
              width: '100%',
              maxWidth: '520px',
              padding: '42px 32px',
              borderRadius: '24px',
              border: '2px dashed #38bdf8',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '42px', marginBottom: '12px' }}>📂</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>
              Drop Engineering Documents, Drawings, or Site Photos
            </div>
            <div style={{ fontSize: '13.5px', color: '#94a3b8', marginTop: '6px' }}>
              Supports PDFs, borehole logs, AGS4, CSV tables, and site inspection images
            </div>
          </div>
        </div>
      )}

      {/* Mobile Backdrop Overlay when Left Sidebar is Open */}
      {isMobile && mobileSidebarOpen && (
        <div
          onClick={() => setMobileSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(4, 6, 12, 0.76)',
            backdropFilter: 'blur(6px)',
            zIndex: 45,
          }}
        />
      )}

      {/* =====================================================================
          1. FLOATING GLASS LEFT SIDEBAR
      ===================================================================== */}
      <aside
        className="civeng-floating-pane"
        style={{
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? '8px' : 0,
          bottom: isMobile ? '8px' : 0,
          left: isMobile ? '8px' : 0,
          transform: isMobile
            ? mobileSidebarOpen
              ? 'translateX(0)'
              : 'translateX(-110%)'
            : 'none',
          width: isMobile ? '284px' : sidebarCollapsed ? '66px' : '278px',
          minWidth: isMobile ? '284px' : sidebarCollapsed ? '66px' : '278px',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s cubic-bezier(0.16, 1, 0.3, 1), transform 0.22s ease',
          userSelect: 'none',
          zIndex: 50,
          overflow: 'hidden',
        }}
      >
        {/* Top Brand + Collapse Hamburger */}
        <div
          style={{
            height: '62px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: !isSidebarExpanded ? 'center' : 'space-between',
            padding: !isSidebarExpanded ? '0' : '0 16px',
            borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
          }}
        >
          {isSidebarExpanded && (
            <div
              onClick={handleStartNewChat}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                cursor: 'pointer',
              }}
            >
              <BrandIcon
                src={BRAND_ASSETS.mainLogo}
                fallbackText="CE"
                size={34}
                borderRadius={9}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#f8fafc', lineHeight: 1.15 }}>
                  CivEng
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  AI Workstation v3.1
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => {
              if (isMobile) setMobileSidebarOpen(false);
              else setSidebarCollapsed((prev) => !prev);
            }}
            title={!isSidebarExpanded ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#cbd5e1',
              cursor: 'pointer',
              padding: '7px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '17px',
            }}
          >
            {isMobile ? '✕' : '☰'}
          </button>
        </div>

        {/* New Engineering Chat + Filter Chats */}
        <div style={{ padding: !isSidebarExpanded ? '12px 8px' : '12px 14px' }}>
          <button
            onClick={handleStartNewChat}
            title="New engineering thread"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: !isSidebarExpanded ? 'center' : 'flex-start',
              gap: '10px',
              padding: !isSidebarExpanded ? '10px 0' : '10px 14px',
              background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.22), rgba(37, 99, 235, 0.16))',
              border: '1px solid rgba(56, 189, 248, 0.55)',
              boxShadow: '0 0 18px rgba(56, 189, 248, 0.18)',
              borderRadius: '12px',
              color: '#f8fafc',
              fontSize: '13.5px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '10px',
            }}
          >
            <span style={{ fontSize: '15px', color: '#7dd3fc' }}>＋</span>
            {isSidebarExpanded && <span>New Engineering Chat</span>}
          </button>

          {isSidebarExpanded ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: 'rgba(10, 15, 26, 0.65)',
                border: '1px solid rgba(148, 163, 184, 0.16)',
                borderRadius: '10px',
              }}
            >
              <span style={{ fontSize: '12px', color: '#64748b' }}>🔍</span>
              <input
                type="text"
                value={searchChatsQuery}
                onChange={(e) => setSearchChatsQuery(e.target.value)}
                placeholder="Filter chat history..."
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: '#e2e8f0',
                  fontSize: '12.5px',
                  width: '100%',
                }}
              />
            </div>
          ) : (
            <button
              onClick={() => setSidebarCollapsed(false)}
              title="Search chats"
              style={{
                width: '100%',
                padding: '8px 0',
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              🔍
            </button>
          )}
        </div>

        {/* Scrollable History + Engineering Studios + Ingested PDFs */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: !isSidebarExpanded ? '4px 6px' : '4px 14px',
          }}
        >
          {isSidebarExpanded && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '10.5px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: '#94a3b8',
                marginBottom: '6px',
                padding: '0 4px',
              }}
            >
              <span>CHAT HISTORY (10D)</span>
              {sessions.length > 0 && (
                <button
                  onClick={handleClearHistory}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#94a3b8',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {filteredSessions.map((sess) => {
            const isActive = sess.id === activeSessionId;
            const tierMeta = getTierMeta(sess.lastTierExecuted);
            return (
              <div
                key={sess.id}
                onClick={() => {
                  setActiveSessionId(sess.id);
                  setStreamingMsgId(null);
                  if (isMobile) setMobileSidebarOpen(false);
                }}
                title={sess.title}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: !isSidebarExpanded ? 'center' : 'flex-start',
                  gap: '10px',
                  padding: !isSidebarExpanded ? '10px 0' : '8px 10px',
                  borderRadius: '10px',
                  backgroundColor: isActive ? 'rgba(56, 189, 248, 0.14)' : 'transparent',
                  border: isActive
                    ? '1px solid rgba(56, 189, 248, 0.4)'
                    : '1px solid transparent',
                  cursor: 'pointer',
                  marginBottom: '4px',
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: tierMeta.dotColor,
                    boxShadow: `0 0 8px ${tierMeta.dotColor}`,
                    flexShrink: 0,
                  }}
                />
                {isSidebarExpanded && (
                  <div style={{ overflow: 'hidden', flex: 1 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        color: isActive ? '#f8fafc' : '#cbd5e1',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {sess.title}
                    </div>
                    <div className="eng-mono" style={{ fontSize: '10.5px', color: '#64748b' }}>
                      {sess.messages.length} messages
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* ENGINEERING STUDIOS */}
          <div style={{ marginTop: '18px' }}>
            {isSidebarExpanded && (
              <div
                style={{
                  fontSize: '10.5px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: '#94a3b8',
                  marginBottom: '8px',
                  padding: '0 4px',
                }}
              >
                ENGINEERING STUDIOS
              </div>
            )}

            {ENGINEERING_TOOLS.map((tool) => (
              <div
                key={tool.id}
                onClick={() => {
                  if (tool.active) {
                    if (isMobile) setMobileSidebarOpen(false);
                    handleLaunchTool(tool.id);
                  }
                }}
                title={`${tool.name} — ${tool.subtitle}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: !isSidebarExpanded ? 'center' : 'space-between',
                  padding: !isSidebarExpanded ? '8px 0' : '8px 10px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(15, 22, 36, 0.55)',
                  border: '1px solid rgba(148, 163, 184, 0.12)',
                  cursor: tool.active ? 'pointer' : 'default',
                  marginBottom: '6px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <BrandIcon
                    src={BRAND_ASSETS.subdomains[tool.id]}
                    fallbackText={tool.code}
                    size={28}
                    borderRadius={7}
                    gradient={tool.bg}
                    glowColor={`${tool.accent}55`}
                  />
                  {isSidebarExpanded && (
                    <span style={{ fontSize: '13px', color: '#f1f5f9', fontWeight: 500 }}>
                      {tool.name}
                    </span>
                  )}
                </div>

                {isSidebarExpanded && (
                  <span
                    className="eng-mono"
                    style={{
                      fontSize: '11px',
                      color: tool.statusTag === '✓' ? '#38bdf8' : '#94a3b8',
                      fontWeight: 600,
                    }}
                  >
                    {tool.statusTag}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Dynamic Ingested Reference PDFs Section */}
          <div style={{ marginTop: '18px' }}>
            {isSidebarExpanded && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '10.5px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: '#94a3b8',
                  marginBottom: '8px',
                  padding: '0 4px',
                }}
              >
                <span>INGESTED PDFS ({ingestedManuals.length})</span>
              </div>
            )}

            {isSidebarExpanded && ingestedManuals.length > 3 && (
              <div style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  value={manualFilterQuery}
                  onChange={(e) => setManualFilterQuery(e.target.value)}
                  placeholder="Filter manuals (GEO, BD, SDM)..."
                  style={{
                    width: '100%',
                    padding: '7px 10px',
                    borderRadius: '9px',
                    backgroundColor: 'rgba(10, 15, 26, 0.65)',
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    color: '#e2e8f0',
                    fontSize: '11.5px',
                    outline: 'none',
                  }}
                />
              </div>
            )}

            {filteredManuals.map((manual) => {
              const isSelected =
                drawerOpen && activeCitation?.doc_slug === manual.doc_code;
              return (
                <div
                  key={manual.doc_code}
                  onClick={() => openManualInDrawer(manual)}
                  title={`${manual.doc_title} (${manual.domain})`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: !isSidebarExpanded ? 'center' : 'space-between',
                    padding: !isSidebarExpanded ? '8px 0' : '9px 11px',
                    borderRadius: '11px',
                    backgroundColor: isSelected
                      ? 'rgba(56, 189, 248, 0.16)'
                      : 'rgba(13, 19, 32, 0.72)',
                    border: isSelected
                      ? '1px solid #38bdf8'
                      : '1px solid rgba(148, 163, 184, 0.14)',
                    cursor: 'pointer',
                    marginBottom: '6px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      overflow: 'hidden',
                    }}
                  >
                    <span style={{ fontSize: '13px', flexShrink: 0, color: '#94a3b8' }}>📄</span>
                    {isSidebarExpanded && (
                      <div style={{ overflow: 'hidden' }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#f8fafc',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {manual.doc_code}
                        </div>
                        <div className="eng-mono" style={{ fontSize: '10.5px', color: '#94a3b8' }}>
                          {manual.total_clauses > 0 ? `${manual.total_clauses} Clauses` : 'PDF Manual'} · {manual.domain}
                        </div>
                      </div>
                    )}
                  </div>
                  {isSidebarExpanded && (
                    <span style={{ fontSize: '11px', color: '#7dd3fc', flexShrink: 0, fontWeight: 600 }}>
                      PDF ↗
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Workspace / Auth Footer */}
        <div
          style={{
            padding: !isSidebarExpanded ? '10px 0' : '12px 16px',
            borderTop: '1px solid rgba(148, 163, 184, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: !isSidebarExpanded ? 'center' : 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: userEmail
                  ? 'linear-gradient(135deg, #10b981, #059669)'
                  : '#1e293b',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 700,
                color: '#fff',
              }}
            >
              HK
            </div>
            {isSidebarExpanded && (
              <span
                style={{
                  fontSize: '12px',
                  color: '#cbd5e1',
                  maxWidth: '130px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userEmail || 'Chartered Workspace'}
              </span>
            )}
          </div>

          {isSidebarExpanded && (
            <button
              onClick={() => (userEmail ? onSignOut?.() : onOpenAuth?.())}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#38bdf8',
                fontSize: '11.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {userEmail ? 'Lock' : 'Unlock'}
            </button>
          )}
        </div>
      </aside>

      {/* =====================================================================
          2. CENTER MAIN COLUMN (Floating Glass Cards Over Galaxy Canvas)
      ===================================================================== */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          maxWidth: '100%',
          position: 'relative',
          height: '100%',
          zIndex: 10,
        }}
      >
        {/* Top Floating Header Bar */}
        {(isMobile || activeSession) && (
          <header
            style={{
              height: '52px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 14px' : '0 18px',
              zIndex: 20,
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              {isMobile && (
                <button
                  onClick={() => setMobileSidebarOpen(true)}
                  className="civeng-floating-pane"
                  style={{
                    color: '#e2e8f0',
                    borderRadius: '9px',
                    padding: '6px 10px',
                    fontSize: '15px',
                    cursor: 'pointer',
                  }}
                >
                  ☰
                </button>
              )}
              <div style={{ overflow: 'hidden' }}>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#f8fafc',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textShadow: '0 2px 10px rgba(0,0,0,0.65)',
                  }}
                >
                  {activeSession ? activeSession.title : 'CivEng Workspace'}
                </div>
              </div>
            </div>

            {/* Right Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {!isMobile && (
                <>
                  <button
                    type="button"
                    onClick={() => handleLaunchTool('drainage')}
                    style={{
                      padding: '5px 11px',
                      borderRadius: '999px',
                      backgroundColor: 'rgba(14, 165, 233, 0.16)',
                      border: '1px solid rgba(56, 189, 248, 0.42)',
                      color: '#7dd3fc',
                      fontSize: '11.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    YLS Drainage ↗
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLaunchTool('borehole')}
                    style={{
                      padding: '5px 11px',
                      borderRadius: '999px',
                      backgroundColor: 'rgba(234, 88, 12, 0.16)',
                      border: '1px solid rgba(251, 146, 60, 0.42)',
                      color: '#fdba74',
                      fontSize: '11.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    3D Boreholes ↗
                  </button>
                </>
              )}

              <button
                onClick={() => {
                  if (drawerOpen) {
                    setDrawerOpen(false);
                  } else if (activeCitation) {
                    setDrawerOpen(true);
                  } else if (ingestedManuals.length > 0) {
                    openManualInDrawer(ingestedManuals[0]);
                  }
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: '999px',
                  backgroundColor: drawerOpen
                    ? 'rgba(56, 189, 248, 0.22)'
                    : 'rgba(15, 21, 35, 0.78)',
                  border: drawerOpen
                    ? '1px solid #38bdf8'
                    : '1px solid rgba(148, 163, 184, 0.25)',
                  color: drawerOpen ? '#7dd3fc' : '#cbd5e1',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backdropFilter: 'blur(12px)',
                }}
              >
                📄 {drawerOpen ? 'Hide PDF' : activeCitation ? `Open PDF (p.${pdfPage})` : 'Manuals PDF'}
              </button>

              {activeSession && (
                <button
                  onClick={handleStartNewChat}
                  title="Return to Home"
                  aria-label="Return to Home"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '999px',
                    backgroundColor: 'rgba(15, 21, 35, 0.78)',
                    border: '1px solid rgba(148, 163, 184, 0.25)',
                    color: '#e2e8f0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  ⌂
                </button>
              )}
            </div>
          </header>
        )}

        {/* Center Body */}
        {!activeSession ? (
          /* -------------------- BENTO GRID HERO VIEW -------------------- */
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: isMobile ? 'flex-start' : 'center',
              padding: isMobile ? '18px 14px 40px 14px' : '28px 24px 48px 24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                marginBottom: '24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <BrandIcon
                  src={BRAND_ASSETS.mainLogo}
                  fallbackText="CE"
                  size={isMobile ? 40 : 46}
                  borderRadius={12}
                />
                <span
                  style={{
                    fontSize: isMobile ? '26px' : '34px',
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    color: '#f8fafc',
                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.75)',
                  }}
                >
                  CivEng Copilot
                </span>
              </div>
            </div>

            {/* Multimodal Hero Composer Card (Phone Rule: Only Ask button sends on mobile) */}
            <div
              className="civeng-floating-pane-glow"
              style={{
                width: '100%',
                maxWidth: '760px',
                borderRadius: '20px',
                padding: isMobile ? '14px 16px' : '16px 20px',
                marginBottom: '28px',
              }}
            >
              {renderPendingAttachmentsStrip()}

              <textarea
                ref={heroTextareaRef}
                rows={3}
                value={inputPrompt}
                onPaste={handlePasteClipboard}
                onChange={(e) => {
                  setInputPrompt(e.target.value);
                  adjustTextareaHeight(e.target, 64, 190);
                }}
                onKeyDown={(e) => {
                  // Rule #2: On phone, Enter inserts a newline; only tapping Ask/Send button submits
                  if (isPhoneInputDevice()) return;
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendPrompt();
                  }
                }}
                placeholder={
                  isMobile
                    ? 'Ask any HK engineering question, run a design check, or attach files... (Tap Ask to send)'
                    : 'Ask any HK engineering question, run a design check, or drop PDFs, borehole logs, or site photos... (Shift+Enter for new line)'
                }
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  color: '#f8fafc',
                  fontSize: isMobile ? '15px' : '15.5px',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '10px',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => unifiedFileInputRef.current?.click()}
                    title="Attach PDF, borehole log, drawing, image, CSV, or AGS4 file"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: 'rgba(20, 29, 48, 0.85)',
                      border: '1px solid rgba(148, 163, 184, 0.22)',
                      borderRadius: '999px',
                      color: '#e2e8f0',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '6px 13px',
                      cursor: 'pointer',
                    }}
                  >
                    <span>📎</span>
                    <span>Attach File / Image</span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      openManualInDrawer(ingestedManuals[0] || DEFAULT_FALLBACK_MANUALS[0])
                    }
                    style={{
                      backgroundColor: 'rgba(56, 189, 248, 0.14)',
                      border: '1px solid rgba(56, 189, 248, 0.38)',
                      borderRadius: '999px',
                      color: '#7dd3fc',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '6px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    📄 {ingestedManuals.length} Manuals
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => handleSendPrompt()}
                  disabled={(!inputPrompt.trim() && pendingAttachments.length === 0) || isLoading}
                  style={{
                    height: '36px',
                    padding: '0 18px',
                    borderRadius: '999px',
                    background:
                      inputPrompt.trim() || pendingAttachments.length > 0
                        ? 'linear-gradient(135deg, #0284c7, #2563eb)'
                        : 'rgba(30, 41, 59, 0.7)',
                    border: '1px solid rgba(125, 211, 252, 0.4)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    cursor:
                      inputPrompt.trim() || pendingAttachments.length > 0
                        ? 'pointer'
                        : 'default',
                    fontSize: '13px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  <span>Ask</span>
                  <span>➤</span>
                </button>
              </div>
            </div>

            {/* INTERACTIVE BENTO GRID: ENGINEERING STUDIOS */}
            <div style={{ width: '100%', maxWidth: '760px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                  padding: '0 4px',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#cbd5e1',
                    letterSpacing: '0.06em',
                  }}
                >
                  INTERACTIVE ENGINEERING STUDIOS & CALCULATORS
                </span>
                <span className="eng-mono" style={{ fontSize: '11px', color: '#38bdf8' }}>
                  FASTMCP VERIFIED
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? '1fr'
                    : 'repeat(2, minmax(0, 1fr))',
                  gap: '12px',
                }}
              >
                {ENGINEERING_TOOLS.map((tool) => (
                  <div
                    key={tool.id}
                    onClick={() => handleLaunchTool(tool.id)}
                    className="bento-card"
                    style={{
                      borderRadius: '16px',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '6px',
                        }}
                      >
                        <BrandIcon
                          src={BRAND_ASSETS.subdomains[tool.id]}
                          fallbackText={tool.code}
                          size={28}
                          borderRadius={7}
                          gradient={tool.bg}
                          glowColor={`${tool.accent}55`}
                        />
                        <span
                          className="eng-mono"
                          style={{
                            fontSize: '10px',
                            padding: '2px 7px',
                            borderRadius: '999px',
                            backgroundColor: 'rgba(255,255,255,0.07)',
                            color: '#cbd5e1',
                          }}
                        >
                          {tool.badge}
                        </span>
                      </div>

                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc' }}>
                        {tool.name}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#94a3b8',
                          marginTop: '3px',
                          lineHeight: 1.45,
                        }}
                      >
                        {tool.subtitle}
                      </div>

                      <div
                        style={{
                          marginTop: '10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: tool.accent,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {tool.id === 'drainage' || tool.id === 'borehole'
                          ? 'Launch Studio ↗'
                          : 'Run Interactive Check →'}
                      </div>
                    </div>

                    <ToolBlueprintIllustration toolId={tool.id} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* -------------------- ACTIVE FLOATING GLASS CHAT THREAD -------------------- */
          <div
            ref={chatScrollContainerRef}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: isMobile
                ? '12px 10px 135px 10px'
                : '18px 24px 145px 24px',
            }}
          >
            <div style={{ maxWidth: '830px', width: '100%', margin: '0 auto' }}>
              {activeSession.messages.map((msg) => {
                if (msg.role === 'user') {
                  return (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        marginBottom: '18px',
                        maxWidth: '100%',
                      }}
                    >
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            marginBottom: '6px',
                            maxWidth: '100%',
                          }}
                        >
                          {msg.attachments.map((att) => (
                            <div
                              key={att.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 10px',
                                borderRadius: '10px',
                                backgroundColor: 'rgba(18, 26, 44, 0.85)',
                                border: '1px solid rgba(56, 189, 248, 0.4)',
                                fontSize: '12px',
                                color: '#e2e8f0',
                                maxWidth: '100%',
                              }}
                            >
                              {att.kind === 'image' && att.previewUrl ? (
                                <img
                                  src={att.previewUrl}
                                  alt={att.name}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <span>📄</span>
                              )}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {att.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div
                        style={{
                          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.28), rgba(59, 130, 246, 0.26))',
                          backdropFilter: 'blur(16px)',
                          border: '1px solid rgba(125, 211, 252, 0.42)',
                          color: '#ffffff',
                          padding: '11px 16px',
                          borderRadius: '18px 18px 4px 18px',
                          maxWidth: isMobile ? '92%' : '78%',
                          fontSize: '14.5px',
                          lineHeight: 1.55,
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
                        }}
                      >
                        {msg.content}
                      </div>
                    </div>
                  );
                }

                const isCurrentlyStreaming = streamingMsgId === msg.id;
                const displayedContent = isCurrentlyStreaming
                  ? msg.content.slice(0, streamedCharCount)
                  : msg.content;

                return (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '24px',
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="civeng-floating-pane-glow"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        borderRadius: '20px',
                        padding: isMobile ? '14px 14px' : '22px 26px',
                      }}
                    >
                      {/* Top Header inside Floating Card */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '8px',
                          marginBottom: '10px',
                          paddingBottom: '8px',
                          borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0, flexWrap: 'wrap' }}>
                          <BrandIcon
                            src={BRAND_ASSETS.mainLogo}
                            fallbackText="CE"
                            size={24}
                            borderRadius={6}
                          />
                          <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#f8fafc' }}>
                            CivEng Copilot
                          </span>
                          {msg.workflowBlueprint && (
                            <span
                              className="eng-mono"
                              style={{
                                fontSize: '10.5px',
                                padding: '2px 8px',
                                borderRadius: '999px',
                                backgroundColor: 'rgba(56, 189, 248, 0.14)',
                                border: '1px solid rgba(56, 189, 248, 0.35)',
                                color: '#7dd3fc',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {msg.workflowBlueprint.component_name} · {msg.workflowBlueprint.current_timeline_stage}
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isCurrentlyStreaming && (
                            <button
                              type="button"
                              onClick={() => setStreamingMsgId(null)}
                              style={{
                                background: 'rgba(56, 189, 248, 0.14)',
                                border: '1px solid rgba(56, 189, 248, 0.35)',
                                borderRadius: '7px',
                                color: '#7dd3fc',
                                fontSize: '11px',
                                padding: '3px 8px',
                                cursor: 'pointer',
                              }}
                            >
                              Skip preview ⏭
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCopyMessage(msg.id, msg.content)}
                            style={{
                              background: 'rgba(15, 23, 42, 0.6)',
                              border: '1px solid rgba(148, 163, 184, 0.2)',
                              borderRadius: '7px',
                              color: '#cbd5e1',
                              fontSize: '11px',
                              padding: '3px 9px',
                              cursor: 'pointer',
                            }}
                          >
                            {copiedMsgId === msg.id ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                      </div>

                      {/* Collapsible Thought Process & Pipeline Route Accordion */}
                      <ThoughtProcessTrace msg={msg} />

                      {/* Structured Block-Level Markdown, Table, Step, Image & KaTeX Renderer */}
                      <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', maxWidth: '100%' }}>
                        {renderFormattedText(displayedContent, msg.citations)}
                      </div>

                      {/* IN-CHAT INTERACTIVE ENGINEERING GRAPH & PARAMETER SLIDERS */}
                      {!isCurrentlyStreaming && (
                        <InteractiveEngineeringStudio
                          artifacts={msg.interactiveArtifacts}
                          proposedMcpAction={msg.proposedMcpAction}
                          onLaunchMcp={handleLaunchMcpAction}
                        />
                      )}

                      {/* REFERENCED MANUAL CLAUSES & TABLES (Fixed Issue #1: Wraps cleanly inside card on all screens) */}
                      {!isCurrentlyStreaming && msg.citations && msg.citations.length > 0 && (
                        <div style={{ marginTop: '18px', width: '100%', maxWidth: '100%' }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: isMobile
                                ? 'minmax(0, 1fr)'
                                : 'repeat(2, minmax(0, 1fr))',
                              gap: '10px',
                              width: '100%',
                              maxWidth: '100%',
                            }}
                          >
                            {msg.citations.slice(0, 4).map((cit, cIdx) => {
                              const isSelected =
                                drawerOpen && activeCitation?.chunk_id === cit.chunk_id;
                              const isPurpleAccent = cIdx === 3;
                              const cardClass = isSelected
                                ? 'civeng-ref-card civeng-ref-card-cyan'
                                : isPurpleAccent
                                ? 'civeng-ref-card civeng-ref-card-purple'
                                : cIdx === 0
                                ? 'civeng-ref-card civeng-ref-card-cyan'
                                : 'civeng-ref-card';

                              return (
                                <div
                                  key={cit.chunk_id}
                                  onClick={() => openCitationDrawer(cit, 'pdf')}
                                  className={cardClass}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '10px',
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <div
                                      style={{
                                        fontSize: '12.5px',
                                        fontWeight: 700,
                                        color: '#f8fafc',
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '6px',
                                        minWidth: 0,
                                      }}
                                    >
                                      <span style={{ color: '#94a3b8', flexShrink: 0 }}>📄</span>
                                      <span
                                        style={{
                                          overflowWrap: 'anywhere',
                                          wordBreak: 'break-word',
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        {cit.doc_slug || cit.doc_title}
                                      </span>
                                      <span
                                        className="eng-mono"
                                        style={{
                                          color: isPurpleAccent ? '#c084fc' : '#38bdf8',
                                          fontSize: '11.5px',
                                          flexShrink: 0,
                                        }}
                                      >
                                        p.{cit.page_number}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        color: '#94a3b8',
                                        marginTop: '3px',
                                        lineHeight: 1.35,
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {cit.clause_id ? `${cit.clause_id}` : 'Manual Reference'}{' '}
                                      {cit.clause_title && cit.clause_title !== cit.clause_id
                                        ? `— ${cit.clause_title}`
                                        : `— ${cit.doc_title}`}
                                    </div>
                                  </div>

                                  <span
                                    style={{
                                      fontSize: '10.5px',
                                      fontWeight: 600,
                                      padding: '4px 8px',
                                      borderRadius: '7px',
                                      backgroundColor: 'rgba(30, 41, 59, 0.75)',
                                      border: '1px solid rgba(148, 163, 184, 0.2)',
                                      color: '#cbd5e1',
                                      flexShrink: 0,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    PDF ↗
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Live Thought Process & Pipeline Stepper while Loading */}
              {isLoading && (
                <div
                  className="civeng-floating-pane animate-pulse-glow"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    color: '#e2e8f0',
                    fontSize: '13px',
                    padding: '14px 18px',
                    borderRadius: '16px',
                    maxWidth: '560px',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#7dd3fc', fontWeight: 700 }}>
                    <span>⚡</span>
                    <span>{LOADING_THOUGHT_STAGES[loadingStageIndex].stage}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
                    {LOADING_THOUGHT_STAGES[loadingStageIndex].detail}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                    {LOADING_THOUGHT_STAGES.map((st, idx) => (
                      <div
                        key={st.stage}
                        style={{
                          flex: 1,
                          height: '3px',
                          borderRadius: '999px',
                          backgroundColor:
                            idx <= loadingStageIndex
                              ? '#38bdf8'
                              : 'rgba(148, 163, 184, 0.2)',
                          transition: 'background-color 0.3s ease',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* ===================================================================
            BOTTOM FLOATING GLASS CHAT COMPOSER (Phone Rule: Only Button Sends)
        =================================================================== */}
        {activeSession && (
          <div
            style={{
              position: 'absolute',
              bottom: isMobile ? '10px' : '14px',
              left: isMobile ? '10px' : '24px',
              right: isMobile ? '10px' : '24px',
              zIndex: 25,
            }}
          >
            <div style={{ maxWidth: '830px', margin: '0 auto' }}>
              <div
                className="civeng-floating-pane-glow"
                style={{
                  borderRadius: '18px',
                  padding: isMobile ? '8px 12px' : '10px 16px',
                }}
              >
                {renderPendingAttachmentsStrip()}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '10px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => unifiedFileInputRef.current?.click()}
                    title="Attach file, PDF, borehole log, or image"
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(20, 29, 48, 0.85)',
                      border: '1px solid rgba(148, 163, 184, 0.22)',
                      color: '#cbd5e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      fontSize: '15px',
                      marginBottom: '1px',
                    }}
                  >
                    📎
                  </button>

                  <textarea
                    ref={chatTextareaRef}
                    rows={1}
                    value={inputPrompt}
                    onPaste={handlePasteClipboard}
                    onChange={(e) => {
                      setInputPrompt(e.target.value);
                      adjustTextareaHeight(e.target, 26, 170);
                    }}
                    onKeyDown={(e) => {
                      // Rule #2: On phone, Enter inserts a newline; only tapping the send button submits
                      if (isPhoneInputDevice()) return;
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendPrompt();
                      }
                    }}
                    placeholder={
                      isMobile
                        ? 'Ask follow-up or attach file... (Tap ➤ to send)'
                        : 'Ask a follow-up question, paste a screenshot, or attach a file... (Shift+Enter for new line)'
                    }
                    style={{
                      flex: 1,
                      minWidth: 0,
                      maxHeight: '170px',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      color: '#f8fafc',
                      fontSize: '14.5px',
                      lineHeight: '1.5',
                      padding: '4px 0',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => handleSendPrompt()}
                    disabled={
                      (!inputPrompt.trim() && pendingAttachments.length === 0) || isLoading
                    }
                    title="Send message"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background:
                        inputPrompt.trim() || pendingAttachments.length > 0
                          ? 'linear-gradient(135deg, #0284c7, #2563eb)'
                          : 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(125, 211, 252, 0.3)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor:
                        inputPrompt.trim() || pendingAttachments.length > 0
                          ? 'pointer'
                          : 'default',
                      fontSize: '15px',
                      flexShrink: 0,
                      marginBottom: '1px',
                    }}
                  >
                    ➤
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* =====================================================================
          3. FLOATING GLASS PDF & CLAUSE DRAWER
          Fixed Issue #5: Renders exact PDF page on Mobile via PDF.js Canvas
      ===================================================================== */}
      {drawerOpen && activeCitation && (
        <aside
          className="civeng-floating-pane"
          style={{
            position: isMobile || isCompact ? 'fixed' : 'relative',
            top: isMobile ? '48px' : 0,
            right: 0,
            bottom: 0,
            left: isMobile ? 0 : undefined,
            width: isMobile
              ? '100vw'
              : drawerExpanded
              ? '54vw'
              : '470px',
            minWidth: isMobile
              ? '100vw'
              : drawerExpanded
              ? '54vw'
              : '410px',
            borderRadius: isMobile ? '20px 20px 0 0' : '20px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.22s ease',
            zIndex: 60,
            overflow: 'hidden',
          }}
        >
          {/* Top Drawer Bar */}
          <div
            style={{
              height: '54px',
              padding: '0 14px',
              borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: '#f8fafc',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {activeCitation.doc_slug || activeCitation.doc_title}
              </span>

              <div
                style={{
                  display: 'flex',
                  backgroundColor: 'rgba(10, 15, 26, 0.75)',
                  borderRadius: '8px',
                  padding: '2px',
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() => setDrawerTab('pdf')}
                  style={{
                    padding: '3px 10px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: drawerTab === 'pdf' ? '#4f46e5' : 'transparent',
                    color: drawerTab === 'pdf' ? '#fff' : '#94a3b8',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  PDF
                </button>
                <button
                  onClick={() => setDrawerTab('clause')}
                  style={{
                    padding: '3px 10px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: drawerTab === 'clause' ? '#4f46e5' : 'transparent',
                    color: drawerTab === 'clause' ? '#fff' : '#94a3b8',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Clause
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <a
                href={getActivePdfSrc()}
                target="_blank"
                rel="noopener noreferrer"
                title="Open PDF in new tab"
                style={{
                  color: '#94a3b8',
                  textDecoration: 'none',
                  fontSize: '14px',
                  padding: '4px 6px',
                }}
              >
                ↗
              </a>
              {!isMobile && (
                <button
                  onClick={() => setDrawerExpanded((prev) => !prev)}
                  title={drawerExpanded ? 'Restore width' : 'Expand viewer'}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '4px 6px',
                  }}
                >
                  ⤢
                </button>
              )}
              <button
                onClick={() => setDrawerOpen(false)}
                title="Close PDF drawer"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: '4px 8px',
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Sub-bar: Clause Title + Prev / Page / Next Controls */}
          <div
            style={{
              padding: '8px 14px',
              borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
              backgroundColor: 'rgba(10, 15, 26, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                color: '#cbd5e1',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {activeCitation.clause_id ? `${activeCitation.clause_id}` : 'Referenced Section'}
              {activeCitation.clause_title &&
              activeCitation.clause_title !== activeCitation.clause_id
                ? ` - ${activeCitation.clause_title}`
                : ` - ${activeCitation.doc_title}`}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: '3px 8px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(20, 29, 48, 0.85)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  color: '#cbd5e1',
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                ◀
              </button>
              <span
                className="eng-mono"
                style={{
                  fontSize: '11.5px',
                  color: '#e2e8f0',
                  padding: '0 6px',
                }}
              >
                p.{pdfPage}
                {pdfTotalPages ? ` / ${pdfTotalPages}` : ''}
              </span>
              <button
                onClick={() =>
                  setPdfPage((p) => (pdfTotalPages ? Math.min(pdfTotalPages, p + 1) : p + 1))
                }
                style={{
                  padding: '3px 8px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(20, 29, 48, 0.85)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  color: '#cbd5e1',
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                ▶
              </button>
            </div>
          </div>

          {/* Drawer Body */}
          <div style={{ flex: 1, position: 'relative', overflowY: 'auto' }}>
            {drawerTab === 'pdf' ? (
              isMobile ? (
                <MobilePdfPageViewer
                  pdfUrl={getActivePdfSrc()}
                  pageNumber={pdfPage}
                  docTitle={activeCitation.doc_slug || activeCitation.doc_title}
                  onTotalPages={(total) => setPdfTotalPages(total)}
                />
              ) : (
                <iframe
                  key={`${activeCitation.doc_slug}-${pdfPage}`}
                  src={getActivePdfSrc()}
                  title={`${activeCitation.doc_title} - Page ${pdfPage}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: '#111622',
                  }}
                />
              )
            ) : (
              <div style={{ padding: '20px' }}>
                <div
                  className="eng-mono"
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#38bdf8',
                    letterSpacing: '0.05em',
                    marginBottom: '6px',
                  }}
                >
                  {(activeCitation.authority || 'HK STATUTORY STANDARD').toUpperCase()} ·
                  PAGE {activeCitation.page_number}
                </div>
                <h3
                  style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#f8fafc',
                    margin: '0 0 14px 0',
                  }}
                >
                  {activeCitation.clause_id ? `${activeCitation.clause_id}: ` : ''}
                  {activeCitation.clause_title || activeCitation.doc_title}
                </h3>

                <div
                  style={{
                    backgroundColor: 'rgba(10, 15, 26, 0.78)',
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                    borderRadius: '12px',
                    padding: '14px 16px',
                  }}
                >
                  {renderFormattedText(activeCitation.content)}
                </div>

                {activeCitation.figure_url && (
                  <div style={{ marginTop: '16px' }}>
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#94a3b8',
                        marginBottom: '8px',
                      }}
                    >
                      EXTRACTED FIGURE SNAPSHOT
                    </div>
                    <img
                      src={activeCitation.figure_url}
                      alt={activeCitation.figure_caption || 'Manual Figure'}
                      style={{
                        maxWidth: '100%',
                        borderRadius: '8px',
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
};

export default Landing;